import type { AiPageGroupRole } from "../registry/schema.js";
import type { AiScreenContent } from "../registry/screen-content.js";
import type { GuideBand } from "./bands.js";
import { GUIDE_CARD_OUTCOMES, type GuideCardOutcome, type GuideCardVariant, type GuideClaim } from "./card-variants.js";
import type { JourneyContext } from "./context.js";
import { renderCollectionProgress, renderMyDueDetail } from "./facts.js";
import { sanitizePayeeLabel } from "../../person-name.js";
import type { GuidePredicate } from "./predicate.js";
import type { AiNotReadyReason, AiReadySubject } from "./ready.js";
import { GUIDE_RESUME_LABEL } from "./resume.js";
import type { GuideNumericSignalKey, GuideSignalKey } from "./signals.js";
import type { GuideTrigger } from "./triggers.js";

/**
 * 🔴 US-AIV2-0061-S01 (@UC0178 @UC0182) — THẺ VIỆC là DỮ LIỆU, không phải mã.
 *
 * Hình dạng `{trigger, roles[], when[], band, priority, templateVi, cta}` chốt ở ADR-0009 và `@UC0217`. 0061 dựng **cơ chế**;
 * **nội dung** thư viện (câu chữ từng tình huống, ma trận trigger × vai cho PO duyệt) là `0065` — nên bảng dưới đây cố ý ít
 * thẻ và chỉ phủ đúng những trigger mà 0061 nối dây (`OPEN` · `ASKED_NEXT` · `DUTY_CREATED`).
 *
 * Luật câu chữ (khoá ở `coverage.invariants.spec.ts`):
 *  - **KHÔNG số tiền VIẾT trong thẻ.** "còn 400.000đ" gõ thẳng vào `templateVi` thì không: số tiền lên màn hình phải đi từ fact của API
 *    (`@UC0151`). 🔴 US-AIV2-0112 H05 (@UC0322): số tiền CHỈ vào câu qua chỗ trống TIỀN của `GUIDE_CONTEXT_VALUES` (`chiTietKhoan` ·
 *    `tienDoDot`), đọc fact loader nạp từ CÙNG cửa GUI, domain chỉ format (`facts.ts`). Một con số tiền sai trong lời chào cũng là nói
 *    dối về tiền — nên danh sách chỗ trống tiền là allowlist ĐÓNG có ratchet (`coverage.invariants.spec.ts` PK-4).
 *  - **KHÔNG tên người** (P5.3 · kênh NHẮC riêng tư tuyệt đối) — chỉ tên NHÓM.
 *  - Thẻ không có nút phải khai `ctaless` kèm LÝ DO; thẻ không nút và không lý do làm người dùng tưởng app hỏng (PK-2 v1).
 */
export interface GuideCard {
  readonly id: string;
  /** Trigger mà thẻ này bắn. Một thẻ dùng cho nhiều trigger là chuyện thường (`OPEN` và `ASKED_NEXT` cùng một trạng thái). */
  readonly triggers: readonly GuideTrigger[];
  /** Vai nhận thẻ; `"*"` = mọi vai KỂ CẢ chưa thuộc nhóm (`role === null`). */
  readonly roles: readonly (AiPageGroupRole | "*")[];
  /** Điều kiện AND thuần trên tín hiệu. Rỗng là không hợp lệ (thẻ bắn cho mọi người mọi lúc). */
  readonly when: readonly GuidePredicate[];
  readonly band: GuideBand;
  /** Cao trước. Chỉ sắp TRONG một bậc — không bao giờ kéo thẻ vượt bậc. */
  readonly priority: number;
  readonly templateVi: string;
  /** Chỗ trống của `templateVi` → tín hiệu SỐ. Kiểu chặn trỏ vào tín hiệu boolean/chuỗi. */
  readonly vars?: Readonly<Record<string, GuideNumericSignalKey>>;
  /**
   * Nút: một trang trong Sổ (chip `run_page`, 0 lời gọi LLM khi bấm). Vắng ⇒ phải khai `ctaless`.
   * 🔴 AI-MENU-CARD-LABELS (QĐ người dùng 28/10 — một việc một tên): `label` = `help.title` của trang đích, TRỪ nút HẸP hơn trang theo cấu trúc
   * (`fill` · `ctaSubject` của thẻ/lối · `shows`) — tên trang sẽ nói sai việc của nút. Lưới tự khám phá: `card-label.invariants.spec.ts`.
   * 🔴 US-AIV2-tourguide-0003 (@UC0361): `fill` = chữ điền sẵn TĨNH cho ô LỰA CHỌN mà CÂU thẻ đã nói rõ ("Xem vai của tôi" ⇒ `chiSo`).
   * Chỉ ô `enum` của trang + giá trị ∈ `choices` (lưới `verified-chip.test.ts` tự khám phá mọi thẻ). Chữ điền theo TRẠNG THÁI (id đợt,
   * id suất) KHÔNG khai ở đây — đó là việc của `ready()` của trang đích.
   */
  readonly cta?: {
    readonly page: string;
    readonly label: string;
    readonly fill?: Readonly<Record<string, string>>;
    /**
     * 🔴 US-AIV2-tourguide-0008 (@UC0362) — nút tới trang `redirect` (mở màn): điều câu thẻ KHẲNG ĐỊNH người dùng sẽ thấy ở màn đó. Lưới
     * `screen-content.invariants.spec.ts` đòi khai + nằm trong nội dung màn (`AI_SCREEN_CONTENT_BY_ROUTE`) — trỏ sai màn ⇒ ĐỎ, nêu tên thẻ.
     */
    readonly shows?: AiScreenContent;
  };
  /**
   * 🔴 US-AIV2-tourguide-0003 (@UC0361 · TG-INV-3) — ĐỐI TƯỢNG mà câu thẻ nói tới, đọc từ fact của ngữ cảnh (`GUIDE_CTA_SUBJECTS`).
   * Trang đích `ready(ctx, subject)` kiểm ĐÚNG đối tượng đó: câu nói "đợt A" thì nút không được dẫn vào "đợt nào?" hay đợt B.
   */
  readonly ctaSubject?: GuideCtaSubjectSource;
  /**
   * 🔴 US-AIV2-tourguide-0007a (@UC0365) — nút ĐỘNG: trang + nhãn + đối tượng đọc từ fact của ngữ cảnh (`GUIDE_CTA_SOURCES`), không khai tĩnh.
   * Dùng cho nút «Làm tiếp» (trang = trang của PHIẾU dở, khác nhau từng người). Thẻ có đúng MỘT trong `cta` · `ctaFrom` · `ctaless`. Mọi lưới
   * đọc `cta` tĩnh (trang có thật, `fill`, vai) không áp — tầng api kiểm cổng quyền + `ready()` trên CHÍNH phiếu (`GuideButtonGate`).
   */
  readonly ctaFrom?: GuideCtaSource;
  readonly ctaless?: "waiting_on_others" | "informational" | "blocked";
  /**
   * 🔴 US-AIV2-tourguide-0004 (@UC0360 · TG-INV-1 · Q3) — mệnh đề sự thật của `templateVi` (biến thể `BASE`) ⇒ cái chứng minh. Lưới
   * `card-variants.test.ts`: mỗi thẻ ≥1 mệnh đề, mỗi chỗ trống số/tiền nằm trong một mệnh đề, mỗi tín hiệu chứng minh nằm trong `when`.
   */
  readonly claims?: readonly GuideClaim[];
  /**
   * 🔴 US-AIV2-tourguide-0004 — biến thể câu theo (tín hiệu, kết quả `ready()` của nút). Thẻ có nút PHẢI phủ mọi kết quả nút có thể có
   * (`guideOutcomesFor(cta.page)`); biến thể không nút không chứa lời mời của nút. Xem `card-variants.ts`.
   */
  readonly variants?: readonly GuideCardVariant[];
  /**
   * 🔴 W-5 UX-MANAGER-SELF-PAY (@UC0377 · N-5) — LỐI ĐÚNG khi nút chính KHÔNG làm được vì một lý do mà một trang KHÁC làm được cho ĐÚNG đối tượng câu nêu
   * (đo 16/10: quản trị `hung` còn nợ ở quỹ không nhận tự báo ⇒ «Bạn còn 2 khoản…» không nút; lối đúng — tự «Ghi nhận đã đóng» khoản của mình — không
   * được chỉ). Không phải cơ chế nút thứ hai: nút lối đúng đi qua CHÍNH cửa nút thẻ (`GuideButtonGate` — cổng quyền ⇒ `ready(ctx, đối tượng)` ⇒
   * `mintVerifiedChip`); qua cửa ⇒ câu `templateVi` của lối đúng + nút; không qua ⇒ câu không nút của `variants` như cũ (Q3). Xem `GuideCardDoor`.
   */
  readonly doors?: readonly GuideCardDoor[];
}

/**
 * 🔴 W-5 UX-MANAGER-SELF-PAY (@UC0377 · N-5) — một LỐI ĐÚNG của thẻ (xem `GuideCard.doors`). Chọn: lối đầu tiên (theo thứ tự khai) có `outcomes` chứa lý do
 * `not_ready` THẬT của nút chính · `when` khớp (tín hiệu không biết ⇒ không khớp) · trang `cta.page` trong cổng của người đọc · đối tượng `ctaSubject` CÓ ·
 * câu nói được. Câu CÓ nút (được mời) — chỉ ra khi nút qua cửa; mệnh đề lời mời chứng minh bằng `door` (cửa của trang lối đúng vừa nói `ready`).
 */
export interface GuideCardDoor {
  /** Id ỔN ĐỊNH `<id thẻ>/<đuôi>` (bảng PO · máy đo). */
  readonly id: string;
  /** Lý do `not_ready` của NÚT CHÍNH mà lối này thay — ⊆ `GUIDE_TARGET_NOT_READY_REASONS[cta.page của thẻ]`. */
  readonly outcomes: readonly AiNotReadyReason[];
  readonly when?: readonly GuidePredicate[];
  /** Trang + nhãn của nút lối đúng (khác trang của nút chính). */
  readonly cta: { readonly page: string; readonly label: string };
  /** Đối tượng mà câu nêu — BẮT BUỘC: lối đúng luôn trỏ đúng đối tượng (TG-INV-3), không bao giờ một trang trơn rồi hỏi lại. */
  readonly ctaSubject: GuideCtaSubjectSource;
  readonly templateVi: string;
  readonly claims: readonly GuideClaim[];
  readonly note?: string;
}

/**
 * 🔴 US-AIV2-tourguide-0003 (@UC0361) — nguồn đối tượng của nút thẻ: tên → hàm đọc fact. Bảng ĐÓNG (thêm nguồn = thêm một dòng ở đây);
 * hàm trả `null` khi fact vắng ⇒ nút không mang đối tượng (trang đích tự quyết hỏi gì).
 */
export const GUIDE_CTA_SUBJECTS = {
  /**
   * US-AIV2-tourguide-0011 (review #1215 MEDIUM-2) — khoản của lời đề nghị đóng hộ mà câu thẻ `DUTY-PAY-ON-BEHALF` nói tới, CHỈ khi đúng MỘT lời
   * đề nghị (≥2 ⇒ không đối tượng — trang tự hỏi). `PAY-ON-BEHALF.ready()` kiểm lại lúc hiện VÀ lúc bấm: đề nghị đã được trả lời/huỷ ⇒ `SUBJECT_GONE`.
   */
  myPayOnBehalf: (ctx: JourneyContext): AiReadySubject | null =>
    ctx.myPayOnBehalfDueIds?.length === 1 && (ctx.myPayOnBehalfTitles?.length ?? 0) === 1 ? { kind: "memberDue", id: ctx.myPayOnBehalfDueIds[0]! } : null,
  /** Đợt mà `{tienDoDot}` của DUTY-COLLECTION-PROGRESS nói tới — CÙNG fact (`openCollectionProgress`), không đọc lần hai. */
  openCollectionProgress: (ctx: JourneyContext): AiReadySubject | null =>
    ctx.openCollectionProgress === null ? null : { kind: "collection", id: ctx.openCollectionProgress.collectionId },
  /**
   * 🔴 W-5 UX-MANAGER-SELF-PAY (@UC0377) — khoản mà `{chiTietKhoan}` của DUTY-MY-UNPAID nêu tên — CÙNG fact (`myNextDue`), không đọc lần hai. Câu không nêu
   * khoản (fact vắng / tên rỗng ⇒ `{chiTietKhoan}` = "") ⇒ không đối tượng ⇒ không lối đúng (câu nói "khoản này" phải có khoản để trỏ).
   */
  myNextDue: (ctx: JourneyContext): AiReadySubject | null => {
    const due = ctx.myNextDue;
    if (due == null || typeof due.memberDueId !== "string" || due.memberDueId === "") return null;
    return GUIDE_CONTEXT_VALUES.chiTietKhoan(ctx) === "" ? null : { kind: "memberDue", id: due.memberDueId };
  },
} as const satisfies Record<string, (ctx: JourneyContext) => AiReadySubject | null>;
export type GuideCtaSubjectSource = keyof typeof GUIDE_CTA_SUBJECTS;

/** Nút ĐỘNG của một thẻ (`GuideCard.ctaFrom`): trang · nhãn · id phiếu được làm tiếp. `null` = fact vắng ⇒ thẻ không có nút (và không nói được). */
export interface GuideDynamicCta {
  readonly page: string;
  readonly label: string;
  /** id PHIẾU dở (khoá `resume` trên payload) — KHÔNG phải đối tượng `ready()` của trang (`subject`). */
  readonly resume: string;
  /** US-AIV2-tourguide-0008 — ô đối tượng ĐÃ CHỐT của phiếu (`GuideUnfinishedTaskFact.subjectSlots`) — nút khác cùng việc nhường «Làm tiếp». */
  readonly resumeSlots?: Readonly<Record<string, string>>;
}

/**
 * 🔴 US-AIV2-tourguide-0007a (@UC0365) — nguồn nút ĐỘNG: tên → hàm đọc fact. Bảng ĐÓNG (thêm nguồn = thêm một dòng). Hôm nay đúng MỘT nguồn:
 * phiếu dở gần nhất ⇒ nút «Làm tiếp» tới trang của phiếu, mang id phiếu.
 */
export const GUIDE_CTA_SOURCES = {
  unfinishedTask: (ctx: JourneyContext): GuideDynamicCta | null =>
    ctx.myUnfinishedTask == null
      ? null
      : {
          page: ctx.myUnfinishedTask.pageId,
          label: GUIDE_RESUME_LABEL,
          resume: ctx.myUnfinishedTask.taskId,
          ...(ctx.myUnfinishedTask.subjectSlots === undefined ? {} : { resumeSlots: ctx.myUnfinishedTask.subjectSlots }),
        },
} as const satisfies Record<string, (ctx: JourneyContext) => GuideDynamicCta | null>;
export type GuideCtaSource = keyof typeof GUIDE_CTA_SOURCES;

/** Thẻ phiếu dở chỉ nói khi nút ra: mọi kết quả KHÔNG nút (lý do · không kiểm được · chính sách) ⇒ biến thể im (xem docblock thẻ). */
export const GUIDE_RESUME_SILENT_OUTCOMES: readonly GuideCardOutcome[] = Object.freeze(GUIDE_CARD_OUTCOMES.filter((o) => o !== "ready"));

/**
 * 🔴 MỘT bảng cho chỗ trống KHÔNG-SỐ — ba chỗ đọc chung (review #826 vòng 2, M10′).
 *
 * Trước bản vá, luật *"`{tenNhom}` lấy từ ngữ cảnh"* nằm ở BA bản chép: danh sách tên ở đây, một `key === "tenNhom"` hardcode
 * trong `renderCardText`, và một bản thứ ba trong `textRenderable`. Thêm chỗ trống thứ hai vào danh sách thì spec xanh còn
 * thẻ **im lặng biến mất** — đúng lớp lỗi "một luật rải ở N bản chép" mà repo đã trả giá nhiều lần.
 *
 * Nay là một `Record<tên → hàm đọc ngữ cảnh>`; danh sách tên **suy ra** từ chính bảng, không khai tay. Hàm trả `null` nghĩa là
 * "ngữ cảnh này không nói được chỗ trống đó" ⇒ thẻ bị loại TRƯỚC khi chọn bậc, không ném giữa lượt.
 */
/**
 * 🔴 MỘT regex nhận diện chỗ trống — bốn chỗ dùng chung (review #826 vòng 3, MEDIUM-1 TS).
 *
 * Vòng 2 đã rút *danh sách* chỗ trống về `GUIDE_CONTEXT_VALUES`, nhưng **cú pháp** nhận diện thì vẫn nằm ở bốn bản chép
 * (`renderCardText` · `textRenderable` · hai spec). Hai chỗ đầu nguy hiểm nhất: `textRenderable` quyết thẻ có ĐỦ ĐIỀU KIỆN,
 * `renderCardText` quyết thẻ NÓI RA SAO. Hai regex lệch nhau một ký tự ⇒ thẻ lọt bộ lọc rồi NÉM giữa lượt — đúng lỗi
 * 500-giữa-lượt mà HIGH-1 vừa vá, chỉ khác đường vào.
 *
 * `g` là cờ có TRẠNG THÁI (`lastIndex`), nên đây là một `RegExp` dùng lại được cho `matchAll`/`replace` (cả hai tự đặt lại
 * `lastIndex`) — đừng dùng nó với `.test()`.
 */
export const GUIDE_PLACEHOLDER_RE = /\{([a-zA-Z][a-zA-Z0-9]*)\}/g;

/**
 * 🔴🔴 US-AIV2-0062 (review #835 vòng 3 MEDIUM-2 · mục L-1+ kế thừa #826) — **chuẩn hoá chuỗi NGƯỜI DÙNG trước khi nó vào câu.**
 *
 * Tên nhóm do người dùng đặt. Biên (`groupNameSchema`) chỉ `.trim().min(1).max(50)` và `.trim()` **không đụng `\n` GIỮA chuỗi**,
 * nên `"Lớp 5A\nBẠN CÒN NỢ 5.000.000đ"` (≤50 ký tự) là một tên nhóm **hợp lệ**. Thế thẳng vào `templateVi` rồi nối bằng `\n` ở
 * `attachGuideLine` ⇒ lượt `done` ra **3 dòng**, dòng giữa là câu do **người khác soạn** nằm trong lượt của TRỢ LÝ — thứ mang
 * tính thẩm quyền trong một app tài chính.
 *
 * Vá ở **chỗ nội suy**, không ở biên: biên nằm trong `packages/contracts` (ngoài phạm vi task này), và quan trọng hơn — **tên
 * nhóm đã lưu từ trước vẫn đi qua đây**. Một phép chuẩn hoá ở chỗ dựng câu bảo vệ được mọi nguồn, kể cả dữ liệu cũ. Siết biên
 * vẫn nên làm (`0062b`), nhưng nó là lớp thứ hai, không thay lớp này.
 *
 * `\s+ → " "` gộp mọi khoảng trắng (gồm `\n`, `\t`, `\u2028`); cắt ở `GUIDE_CONTEXT_VALUE_MAX` để một tên bất thường không
 * đẩy cả lượt thành một đoạn văn. Tên bình thường **không bị đụng tới**.
 */
export const GUIDE_CONTEXT_VALUE_MAX = 60;

export function normalizeGuideContextValue(raw: string): string {
  // 🔴 FIX-1214 (security M2): tên nhóm/đợt/người mời do NGƯỜI KHÁC đặt (lời mời — ranh giới chéo người dùng) ⇒ bỏ ký tự không in / hướng / vô
  // hình bằng MỘT phép quét (`sanitizePayeeLabel`, cùng chốt nhãn) TRƯỚC khi gộp/cắt. Phủ mọi thẻ: `tenNhom`, `tenNhomCho`, `renderPendingInvite`.
  const flat = sanitizePayeeLabel(raw).replace(/\s+/gu, " ").trim();
  return flat.length <= GUIDE_CONTEXT_VALUE_MAX ? flat : `${flat.slice(0, GUIDE_CONTEXT_VALUE_MAX - 1)}…`;
}

export const GUIDE_CONTEXT_VALUES = Object.freeze({
  /** Tên NHÓM được phép nêu; tên NGƯỜI thì không (P5.3 — kênh nhắc riêng tư tuyệt đối). Chuẩn hoá: xem trên. */
  tenNhom: (ctx: JourneyContext): string | null => (ctx.groupName === null ? null : normalizeGuideContextValue(ctx.groupName) || null),
  /**
   * 🔴 US-AIV2-0111 C07g (@UC0216) — tên NHÓM mà người này đang CHỜ DUYỆT vào (chỉ khi đúng một nhóm — `pendingJoinGroupName`). Tên
   * nhóm do người dùng đặt ⇒ cùng phép chuẩn hoá `tenNhom` (một tên nhóm có `\n` không được chen câu vào lượt của trợ lý).
   */
  tenNhomCho: (ctx: JourneyContext): string | null =>
    ctx.pendingJoinGroupName === null ? null : normalizeGuideContextValue(ctx.pendingJoinGroupName) || null,
  /**
   * 🔴 US-AIV2-0113 M05 (@UC0214) — CẢ CÂU lời mời đang chờ: tên NHÓM + (đúng một lời mời) tên NGƯỜI MỜI. Một nguồn cho lời chào (thẻ
   * `INVITE-PENDING-MINE`) và cho lượt hỏi "tôi có lời mời nào không" (`AiDoerService`, trang `INVITE-ACCEPT`) — xem `renderPendingInvite`.
   */
  cauLoiMoi: (ctx: JourneyContext): string | null =>
    renderPendingInvite({ count: ctx.myInviteCount, groupNames: ctx.pendingInviteGroupNames, inviterName: ctx.pendingInviterName }),
  /**
   * 🔴 US-AIV2-0112 H05 (@UC0322) — chỗ trống TIỀN #1: chi tiết khoản CỦA TÔI gần hạn nhất (tên · còn thiếu · hạn), kèm đúng dấu nối.
   *
   * 🔴 Trả `""` (KHÔNG `null`) khi fact vắng: chỗ trống này là PHẦN THÊM của một lời nhắc tiền đã đúng từ trước — fact đọc hỏng thì câu
   * rơi về đúng câu cũ, không được làm câm cả thẻ `DUTY-MY-UNPAID` (`null` ⇒ `textRenderable` loại thẻ ⇒ người đang nợ không nghe gì).
   * 1 khoản ⇒ `": <chi tiết>"`; ≥2 ⇒ `", gần hạn nhất: <chi tiết>"` (khoản không hạn ⇒ `", trong đó có: …"`) — không cộng tổng (phép cộng
   * tiền thứ hai).
   */
  chiTietKhoan: (ctx: JourneyContext): string | null => {
    const due = ctx.myNextDue;
    // `== null`: fact vắng HOẶC ngữ cảnh dựng tay thiếu trường ⇒ câu cũ (không ném giữa lượt chào).
    if (due == null) return "";
    const title = normalizeGuideContextValue(due.title);
    if (title === "") return "";
    // UX-GUIDE-POLISH MED-6: hai suất của mình cùng tên đợt ⇒ «<đợt> (<bé>)» (khuôn `{tenKhoanNopHo}` của G-3). Tên rỗng sau chuẩn hoá ⇒ không «()».
    const seat = due.seatName === undefined ? "" : normalizeGuideContextValue(due.seatName);
    const detail = renderMyDueDetail(due, seat === "" ? title : `${title} (${seat})`);
    if (ctx.myUnpaidDueCount === 1) return `: ${detail}`;
    // review #1064 L2: khoản được chọn KHÔNG có hạn (⇒ mọi khoản đều không hạn — sắp hạn-null-sau-cùng) thì "gần hạn nhất" là lời khai sai.
    // review 0004 M1: khoản được nêu là khoản đợt MỞ ưu tiên — không phải khoản gần hạn nhất của MỌI khoản (`nearestOfAll === false`) ⇒ "trong đó có".
    return due.daysLeft === null || due.nearestOfAll === false ? `, trong đó có: ${detail}` : `, gần hạn nhất: ${detail}`;
  },
  /**
   * 🔴 US-AIV2-0112 H05 (@UC0322) — chỗ trống TIỀN #2: tiến độ đợt đang mở gần nhất (đã đóng/mẫu số · đã thu/cần thu · hạn). Fact vắng ⇒
   * `null` ⇒ thẻ bị loại (đây là CẢ nội dung của thẻ — không có "câu cũ" nào để rơi về; im còn hơn nói 0/0).
   */
  tienDoDot: (ctx: JourneyContext): string | null => {
    const p = ctx.openCollectionProgress;
    if (p == null) return null;
    const title = normalizeGuideContextValue(p.title);
    return title === "" ? null : renderCollectionProgress(p, title);
  },
  /**
   * 🔴 US-AIV2-tourguide-0011 (@UC0371) — tên các khoản chi đang chờ CHÍNH người này bỏ phiếu (`myOpenVoteTitles`). Tên do thành viên đặt ⇒
   * chuẩn hoá + cắt TỪNG tên rồi bọc «…» (chữ của người dùng, không phải lời trợ lý — cùng luật lời mời). Chưa đặt tên ⇒ "(chưa đặt tên)",
   * không bịa. ≤ `GUIDE_NAME_LIST_MAX` tên, nhiều hơn ⇒ ", …". Rỗng ⇒ `null` (thẻ không nói được ⇒ loại, không ném).
   */
  tenKhoanChi: (ctx: JourneyContext): string | null =>
    guideNameList((ctx.myOpenVoteTitles ?? []).map((t) => {
      const n = t === null ? "" : normalizeGuideContextValue(t);
      return n === "" ? GUIDE_UNNAMED : quoteUserText(n);
    })),
  /** 🔴 US-AIV2-tourguide-0011 (@UC0371) — tên đợt (hoặc quỹ) của khoản có lời đề nghị đóng hộ chờ người này trả lời — CÙNG cách gọi khoản của thẻ nợ. */
  // G-3 LOW (@UC0371): bỏ mục TRÙNG (hai người đề nghị đóng hộ cùng một khoản) — thẻ đã nói SỐ lời đề nghị ở `{soDeNghi}`; liệt kê một tên hai lần
  // là câu lặp («Quỹ lớp tháng 10, Quỹ lớp tháng 10»). Tên đã kèm suất khi hai bé cùng đợt (loader `payOnBehalfCardTitles`).
  tenKhoanNopHo: (ctx: JourneyContext): string | null =>
    guideNameList([...new Set((ctx.myPayOnBehalfTitles ?? []).map((t) => normalizeGuideContextValue(t) || GUIDE_UNNAMED))]),
  /**
   * 🔴 AI-SO-DRIFT-1012 (@UC0217 @UC0035) — tên các khoản chi chờ CHÍNH người này duyệt thẳng được (`pendingExpenseApprovalTitles`). CÙNG luật
   * `tenKhoanChi`: chữ người dùng đặt ⇒ chuẩn hoá + cắt từng tên, bọc «…»; chưa đặt tên ⇒ "(chưa đặt tên)"; rỗng ⇒ `null` (thẻ không nói được ⇒ loại).
   * Tên KHOẢN CHI, không tên người gửi (người gửi hiện ở phiếu xác nhận — `{nguoiGui}` của EXP-APPROVE).
   */
  tenKhoanChiDuyet: (ctx: JourneyContext): string | null =>
    guideNameList((ctx.pendingExpenseApprovalTitles ?? []).map((t) => {
      const n = t === null ? "" : normalizeGuideContextValue(t);
      return n === "" ? GUIDE_UNNAMED : quoteUserText(n);
    })),
  /**
   * 🔴 US-AIV2-tourguide-0007a (@UC0365) — việc ĐANG DỞ: «tên việc» (chữ của Sổ) + ` cho «đợt/quỹ»` khi phiếu đã chốt đối tượng. Nhãn đối tượng do
   * người dùng đặt ⇒ chuẩn hoá + cắt (`normalizeGuideContextValue` — `\n` không chen câu vào lượt trợ lý). Fact vắng ⇒ `null` (thẻ không nói được).
   */
  viecDo: (ctx: JourneyContext): string | null => {
    const t = ctx.myUnfinishedTask;
    if (t == null) return null;
    const title = normalizeGuideContextValue(t.title);
    if (title === "") return null;
    const subject = t.subjectLabel === null ? "" : normalizeGuideContextValue(t.subjectLabel);
    return subject === "" ? quoteUserText(title) : `${quoteUserText(title)} cho ${quoteUserText(subject)}`;
  },
  /** US-AIV2-tourguide-0006 (@UC0366 · Q7) — giờ lần nhắc TAY gần nhất của đợt trong câu tiến độ (`manualRemind`, loader format). Vắng ⇒ `null`. */
  lucNhac: (ctx: JourneyContext): string | null => ctx.openCollectionProgress?.manualRemind?.at ?? null,
  /** US-AIV2-tourguide-0006 (@UC0366 · Q7) — từ lúc nào Người dẫn đường mời nhắc lại đợt đó (lần nhắc TAY + `GUIDE_REMIND_AGAIN_DAYS`). */
  ngayNhacLai: (ctx: JourneyContext): string | null => ctx.openCollectionProgress?.manualRemind?.againFrom ?? null,
} satisfies Record<string, (ctx: JourneyContext) => string | null>);

/** US-AIV2-tourguide-0011 — số tên tối đa trong một câu liệt kê tên khoản (câu dài thì người đọc không đọc). */
export const GUIDE_NAME_LIST_MAX = 3;
/** Khoản chưa có tên — nói thật là chưa đặt tên, không bịa. */
const GUIDE_UNNAMED = "(chưa đặt tên)";

function guideNameList(names: readonly string[]): string | null {
  if (names.length === 0) return null;
  const shown = names.slice(0, GUIDE_NAME_LIST_MAX).join(", ");
  return names.length > GUIDE_NAME_LIST_MAX ? `${shown}, …` : shown;
}

/** Lời mời đang chờ của CHÍNH người đọc — đúng ba đại lượng loader đọc từ cửa màn "Lời mời của tôi" (US-AIV2-0113 M05). */
export interface PendingInviteFact {
  /** Số NHÓM đang mời (có trần — loader quét một trang); phân "không / một / nhiều" và quyết "…". */
  readonly count: number;
  /** Tên TỪNG nhóm (loader đưa tối đa 3, mới nhất trước). `[]` ⇒ không nói (không bịa). */
  readonly groupNames: readonly string[];
  /** Họ tên người mời — chỉ có nghĩa khi `count === 1`. */
  readonly inviterName: string | null;
}

/** «…» — tên do NGƯỜI KHÁC đặt nằm trong ngoặc kép góc: đọc ra là chữ của người dùng, không phải lời của trợ lý (review #1103 M3). */
function quoteUserText(v: string): string {
  return `«${v}»`;
}

/**
 * 🔴 US-AIV2-0113 M05 (@UC0214) — câu *"Bạn có lời mời vào nhóm «FC Văn Phòng» («Trần Minh Tuấn» mời)."* — MỘT bộ dựng cho mọi chỗ nói lời mời.
 *
 * `null` khi không có gì đúng để nói: 0 lời mời · không tên nhóm nào (không bịa). Tên nhóm và tên người do người dùng đặt ⇒ chuẩn hoá + cắt
 * TỪNG tên (`normalizeGuideContextValue`, review #1103 L2 — cắt chuỗi đã nối sẽ nuốt tên sau) rồi bọc «…» (M3). ≥2 lời mời ⇒ chỉ tên các
 * nhóm: gán tên MỘT người mời cho cả danh sách là lời khai sai; đếm nhiều hơn số tên ⇒ "…". Thiếu tên người mời ⇒ bỏ phần "(… mời)".
 */
export function renderPendingInvite(fact: PendingInviteFact): string | null {
  if (!(fact.count > 0)) return null;
  const names = fact.groupNames.map((n) => normalizeGuideContextValue(n)).filter((n) => n !== "");
  if (names.length === 0) return null;
  if (fact.count > 1) {
    const more = fact.count > names.length ? ", …" : "";
    return `Bạn có lời mời vào các nhóm ${names.map(quoteUserText).join(", ")}${more}.`;
  }
  const group = quoteUserText(names[0]!);
  const inviter = fact.inviterName === null ? "" : normalizeGuideContextValue(fact.inviterName);
  return inviter === "" ? `Bạn có lời mời vào nhóm ${group}.` : `Bạn có lời mời vào nhóm ${group} (${quoteUserText(inviter)} mời).`;
}

/**
 * 🔴 US-AIV2-0112 H05 (@UC0322) — chỗ trống MANG SỐ TIỀN. Allowlist ĐÓNG: `coverage.invariants.spec.ts` PK-4 đòi mọi chỗ trống ngữ cảnh
 * sinh ra chuỗi có số tiền phải có tên ở đây, và danh sách chỉ nới bằng QĐ có tên (H05 — QĐ PO 28/09: câu thẻ không cần PO duyệt, số chèn
 * từ fact block).
 */
export const GUIDE_MONEY_PLACEHOLDERS: readonly string[] = Object.freeze(["chiTietKhoan", "tienDoDot"]);

/** Tên các chỗ trống không-số — SUY từ bảng trên, không khai tay (bản chép thứ hai sẽ lệch). */
export const GUIDE_CONTEXT_PLACEHOLDERS: readonly string[] = Object.freeze(Object.keys(GUIDE_CONTEXT_VALUES));

/**
 * 🔴 US-AIV2-tourguide-0006 (@UC0366) — chỗ trống KHÔNG-SỐ chỉ có giá trị khi một tín hiệu đúng (fact tuỳ chọn). Biến thể dùng chúng PHẢI khai
 * tín hiệu đó `truthy` trong `when` (lưới `card-variants.test.ts`) ⇒ biến thể được chọn thì nói được (không câm thẻ, không ném giữa lượt).
 */
export const GUIDE_CONTEXT_VALUE_GUARDS: Readonly<Partial<Record<keyof typeof GUIDE_CONTEXT_VALUES, GuideSignalKey>>> = Object.freeze({
  lucNhac: "openCollectionRemindedRecently",
  ngayNhacLai: "openCollectionRemindedRecently",
});

/**
 * GUIDE-FIX-20261020 (máy đo audit-after2 · Q1) — thẻ «nhóm chưa có số tài khoản nhận tiền». Biến thể `…/q1*` của `DUTY-MY-UNPAID` khai câu thay
 * `withCard` trỏ nó: chỉ khi dòng của thẻ này THẬT SỰ đứng cùng lượt (`guideTextsWithCards`) câu nợ mới bỏ vế «chưa có tài khoản» (không nghe hai lần);
 * lượt một dòng / thẻ này im ⇒ câu nợ giữ vế đó (Q1 — không ngầm hứa có chỗ chuyển tiền).
 */
const GUIDE_BANK_CARD_ID = "DUTY-NO-BANK-ACCOUNT";

/**
 * 🔴 `Object.freeze<GuideCard[]>` — tham số kiểu TƯỜNG MINH, đừng gỡ.
 *
 * Không có nó, TS suy kiểu phần tử từ chính mảng rồi hợp nhất thành một UNION: thẻ có `vars` và thẻ không có `vars`
 * gộp lại thành `{ soViec: … ; soDot?: undefined }`, và với `exactOptionalPropertyTypes` thì union đó **không gán
 * được** vào `GuideCard` — cả ba gói (`domain`/`api`/`worker`) đỏ typecheck ngay khi 0063 thêm thẻ có chỗ trống MỚI.
 * Tham số kiểu bật **contextual typing**: mỗi thẻ được kiểm RIÊNG với `GuideCard`, nên lỗi (nếu có) chỉ ra đúng thẻ sai
 * thay vì một thông báo về cả mảng.
 */
export const GUIDE_CARD_LIBRARY: readonly GuideCard[] = Object.freeze<GuideCard[]>([
  /**
   * 🔴🔴 US-AIV2-0062 (@UC0180) — BỊ CHẶN thì cho LỐI RẼ, đừng trả "bạn không có quyền" trần.
   *
   * Cổng năng lực vẫn fail-closed y nguyên (403 ở `assertCanStart`/`assertCanConfirm`) — thẻ này KHÔNG nới một quyền nào. Nó chỉ thay
   * **câu nói** ở đầu kia của cùng một lần từ chối: ngõ cụt ⇒ một câu nói rõ AI làm được việc này, kèm nút xem ai giữ vai gì.
   *
   * 🔴 Nêu VAI, tuyệt đối không nêu TÊN NGƯỜI (P5.3 — kênh nhắc riêng tư tuyệt đối; lưới `coverage.invariants` cấm ở tầng dữ liệu).
   * Nút trỏ `READ-PEOPLE-MINE` (`roles: ALL_ROLES`, tier `read`) — trang duy nhất trong Sổ hôm nay trả lời đúng câu "ai giữ vai gì".
   */
  {
    id: "BLOCKED-ASK-MANAGER",
    triggers: ["BLOCKED"],
    roles: ["*"],
    // 🔴 US-AIV2-0062b: CHỈ cho người CÓ ghế ở nhóm đang hỏi. Người không ghế không xem được ai giữ vai gì ở nhóm đó — nút này
    // với họ là một đường vòng không tới nơi (đo trên máy ảo 26/09). Họ nhận `BLOCKED-NOT-IN-GROUP` ngay dưới.
    when: [
      { signal: "hasAnyGroup", op: "truthy" },
      { signal: "askedGroupNoSeat", op: "falsy" },
    ],
    band: "BLOCKED",
    priority: 100,
    // 🔴 Câu phải ĐÚNG với MỌI vai, không chỉ member. Quản trị viên cũng bị chặn được (quỹ đóng băng · nhóm đã đóng sổ · cờ trang
    // tắt), và nói với một chủ nhóm rằng "chỉ quản trị làm được" là nói sai. Nên câu nêu **sự kiện** ("chưa mở cho bạn ở nhóm này")
    // chứ không nêu **nguyên nhân** — nguyên nhân cần một tín hiệu mà loader chưa đếm, và bịa nguyên nhân là nói dối.
    // Câu chữ theo từng nguyên nhân là `US-AIV2-0065` (thư viện tình huống).
    templateVi: "Việc này chưa mở cho bạn ở nhóm này. Bạn xem ai đang giữ vai nào để nhờ đúng người nhé.",
    // US-AIV2-tourguide-0003 (K14): câu đã nói muốn xem gì ⇒ nút không hỏi lại 4 lựa chọn.
    cta: { page: "READ-PEOPLE-MINE", label: "Xem ai giữ vai gì", fill: { chiSo: "thành viên" } },
    claims: [
      { clause: "Việc này chưa mở cho bạn ở nhóm này", proof: "trigger" },
      { clause: "Bạn xem ai đang giữ vai nào", proof: "door" },
    ],
    // US-AIV2-tourguide-0004 (Q3): nút không dựng được ⇒ chỉ nói sự thật, không mời "xem".
    variants: [{ id: "BLOCKED-ASK-MANAGER/khong-nut", outcomes: ["unknown"], templateVi: "Việc này chưa mở cho bạn ở nhóm này.", claims: [{ clause: "Việc này chưa mở cho bạn ở nhóm này", proof: "trigger" }] }],
  },
  /**
   * 🔴🔴 US-AIV2-0062b (@UC0265) — người KHÔNG có ghế ở nhóm đang hỏi: nói sự thật về CHÍNH họ + một đường đi làm được.
   *
   * Sau `0065d` đây là tập đông nhất rơi vào lượt `BLOCKED` ở lời chào (bị cho ra khỏi nhóm khi màn chào đang mở · chọn một nhóm
   * đã rời · nhóm không tồn tại). Đường đi thật duy nhất của họ là **link mời** — cơ chế có sẵn, người TRONG nhóm gửi.
   *
   * 🔴 KHÔNG nút: không trang nào của Sổ làm được việc "xin vào lại một nhóm" từ phía người ngoài, và "nhắn người" không có kênh
   * (QĐ 0062b). Menu toàn cục vẫn đi kèm lượt này (`menuTurn`), nên đây không phải ngõ cụt — chỉ là thẻ không hứa điều nó không
   * làm được. `waiting_on_others`: bước tiếp theo nằm ở người trong nhóm.
   *
   * 🔴 KHÔNG `{tenNhom}`: ngữ cảnh của lượt này là TOÀN CỤC có chủ đích (`noDoorHere`), và câu phải y hệt cho nhóm không tồn tại
   * / tắt trợ lý / bật trợ lý — một chữ khác nhau là một bit rò (`@UC0263`).
   */
  {
    id: "BLOCKED-NOT-IN-GROUP",
    triggers: ["BLOCKED"],
    roles: ["*"],
    when: [
      { signal: "hasAnyGroup", op: "truthy" },
      { signal: "askedGroupNoSeat", op: "truthy" },
    ],
    band: "BLOCKED",
    priority: 100,
    templateVi: "Bạn chưa ở trong nhóm này, nên mình chưa làm giúp được việc gì ở đó. Muốn tham gia, bạn nhờ ai đó trong nhóm gửi link mời nhé.",
    ctaless: "waiting_on_others",
    claims: [{ clause: "Bạn chưa ở trong nhóm này", proof: "askedGroupNoSeat" }],
  },
  // ——— SETUP: thang khởi tạo. Mỗi bậc một thẻ, điều kiện loại trừ nhau bằng chính DỮ LIỆU (không luật triệt tiêu tay).
  {
    id: "SETUP-NO-GROUP",
    // `BLOCKED` ở đây KHÔNG phải cho đủ ô: người chưa có nhóm bị cổng chặn là bị chặn **chính vì** chưa có nhóm
    // (`AI_GROUP_REQUIRED`), nên "Tạo nhóm" đúng là lối rẽ của họ — không phải "đi nhờ người khác".
    triggers: ["OPEN", "ASKED_NEXT", "TASK_DONE", "BLOCKED", "RETURN_AFTER_ABSENCE"],
    roles: ["*"],
    when: [{ signal: "hasAnyGroup", op: "falsy" }],
    band: "SETUP",
    priority: 90,
    templateVi: "Bạn chưa có nhóm nào. Tạo nhóm để bắt đầu ghi sổ chung — ai trong nhóm cũng nhìn thấy cùng một cuốn sổ.",
    cta: { page: "ONB-CREATE-GROUP", label: "Tạo nhóm" },
    claims: [
      { clause: "Bạn chưa có nhóm nào", proof: "hasAnyGroup" },
      { clause: "Tạo nhóm để bắt đầu ghi sổ chung", proof: "door" },
    ],
    variants: [{ id: "SETUP-NO-GROUP/khong-nut", outcomes: ["unknown"], templateVi: "Bạn chưa có nhóm nào.", claims: [{ clause: "Bạn chưa có nhóm nào", proof: "hasAnyGroup" }] }],
  },
  {
    id: "SETUP-NO-FUND",
    triggers: ["OPEN", "ASKED_NEXT", "TASK_DONE", "RETURN_AFTER_ABSENCE"],
    roles: ["owner", "admin"],
    when: [
      { signal: "hasAnyGroup", op: "truthy" },
      { signal: "fundCount", op: "eq", value: 0 },
    ],
    band: "SETUP",
    priority: 80,
    templateVi: "Nhóm {tenNhom} chưa có quỹ nào. Bạn tạo quỹ trước thì mới thu tiền được nhé.",
    cta: { page: "ONB-CREATE-FUND", label: "Thêm quỹ cho nhóm" },
    claims: [
      { clause: "chưa có quỹ nào", proof: "fundCount" },
      { clause: "Bạn tạo quỹ trước", proof: "door" },
    ],
    variants: [{ id: "SETUP-NO-FUND/khong-nut", outcomes: ["unknown"], templateVi: "Nhóm {tenNhom} chưa có quỹ nào.", claims: [{ clause: "chưa có quỹ nào", proof: "fundCount" }] }],
  },
  {
    id: "SETUP-NO-MEMBER",
    triggers: ["OPEN", "ASKED_NEXT", "TASK_DONE", "RETURN_AFTER_ABSENCE"],
    roles: ["owner", "admin"],
    when: [
      { signal: "fundCount", op: "gt", value: 0 },
      { signal: "memberCount", op: "lte", value: 1 },
    ],
    band: "SETUP",
    priority: 70,
    // US-AIV2-tourguide-0004: câu MẶC ĐỊNH không nói "mới có mình bạn" (số lời mời đang chờ chưa biết) — biến thể dưới nói khi đã biết.
    templateVi: "Nhóm {tenNhom} chưa có ai vào cùng bạn. Bạn thêm thành viên để cùng chia khoản đóng nhé.",
    cta: { page: "ONB-ADD-MEMBERS", label: "Thêm thành viên" },
    claims: [
      { clause: "chưa có ai vào cùng bạn", proof: "memberCount" },
      { clause: "Bạn thêm thành viên để cùng chia khoản đóng", proof: "door" },
    ],
    variants: [
      {
        id: "SETUP-NO-MEMBER/co-loi-moi",
        outcomes: ["ready"],
        when: [{ signal: "groupPendingInviteCount", op: "gt", value: 0 }],
        vars: { soLoiMoi: "groupPendingInviteCount" },
        templateVi: "Nhóm {tenNhom} chưa có ai vào cùng bạn — {soLoiMoi} lời mời đã gửi đang chờ người nhận. Bạn có thể thêm thành viên khác để chia khoản đóng.",
        claims: [
          { clause: "chưa có ai vào cùng bạn", proof: "memberCount" },
          { clause: "{soLoiMoi} lời mời đã gửi đang chờ người nhận", proof: "groupPendingInviteCount" },
          { clause: "Bạn có thể thêm thành viên khác", proof: "door" },
        ],
        note: "Không nói «mới có mình bạn» khi đã gửi lời mời (§1 SETUP-NO-MEMBER).",
      },
      {
        id: "SETUP-NO-MEMBER/mot-minh",
        outcomes: ["ready"],
        when: [{ signal: "groupPendingInviteCount", op: "eq", value: 0 }],
        templateVi: "Nhóm {tenNhom} mới có mình bạn. Bạn thêm thành viên để cùng chia khoản đóng nhé.",
        claims: [
          { clause: "mới có mình bạn", proof: "memberCount" },
          { clause: "Bạn thêm thành viên để cùng chia khoản đóng", proof: "door" },
        ],
      },
      {
        id: "SETUP-NO-MEMBER/khong-them-ho-co-loi-moi",
        outcomes: ["GROUP_KIND_UNSUPPORTED"],
        when: [{ signal: "groupPendingInviteCount", op: "gt", value: 0 }],
        vars: { soLoiMoi: "groupPendingInviteCount" },
        templateVi: "Nhóm {tenNhom} chưa có ai vào cùng bạn — {soLoiMoi} lời mời đã gửi đang chờ người nhận.",
        claims: [
          { clause: "chưa có ai vào cùng bạn", proof: "memberCount" },
          { clause: "{soLoiMoi} lời mời đã gửi đang chờ người nhận", proof: "groupPendingInviteCount" },
        ],
      },
      {
        id: "SETUP-NO-MEMBER/khong-them-ho",
        outcomes: ["GROUP_KIND_UNSUPPORTED"],
        templateVi: "Nhóm {tenNhom} chưa có ai vào cùng bạn. Ở nhóm này mình chưa thêm tên hộ được — bạn thêm người ở mục Thành viên của nhóm nhé.",
        claims: [
          { clause: "chưa có ai vào cùng bạn", proof: "memberCount" },
          { clause: "Ở nhóm này mình chưa thêm tên hộ được", proof: "door" },
        ],
        note: "Cùng ý câu lý do GROUP_KIND_UNSUPPORTED của catalog lúc bấm.",
      },
      { id: "SETUP-NO-MEMBER/khong-nut", outcomes: ["unknown"], templateVi: "Nhóm {tenNhom} chưa có ai vào cùng bạn.", claims: [{ clause: "chưa có ai vào cùng bạn", proof: "memberCount" }] },
    ],
  },
  {
    id: "SETUP-NO-COLLECTION",
    triggers: ["OPEN", "ASKED_NEXT", "TASK_DONE", "RETURN_AFTER_ABSENCE"],
    roles: ["owner", "admin", "treasurer"],
    when: [
      { signal: "memberCount", op: "gt", value: 1 },
      { signal: "openCollectionCount", op: "eq", value: 0 },
    ],
    band: "SETUP",
    priority: 60,
    templateVi: "Nhóm {tenNhom} chưa có đợt thu nào đang mở. Bạn mở một đợt thu để mọi người biết cần đóng bao nhiêu nhé.",
    cta: { page: "COL-EQUAL", label: "Mở đợt thu đều" },
    claims: [
      { clause: "chưa có đợt thu nào đang mở", proof: "openCollectionCount" },
      { clause: "Bạn mở một đợt thu", proof: "door" },
    ],
    variants: [
      {
        id: "SETUP-NO-COLLECTION/khong-quy",
        outcomes: ["NO_FUND_TO_COLLECT"],
        templateVi: "Nhóm {tenNhom} chưa có đợt thu nào đang mở, và lúc này chưa có quỹ nào mở được đợt thu.",
        claims: [
          { clause: "chưa có đợt thu nào đang mở", proof: "openCollectionCount" },
          { clause: "chưa có quỹ nào mở được đợt thu", proof: "door" },
        ],
        note: "K13 — cùng ý câu lý do NO_FUND_TO_COLLECT của catalog lúc bấm.",
      },
      { id: "SETUP-NO-COLLECTION/khong-nut", outcomes: ["unknown"], templateVi: "Nhóm {tenNhom} chưa có đợt thu nào đang mở.", claims: [{ clause: "chưa có đợt thu nào đang mở", proof: "openCollectionCount" }] },
    ],
  },
  // ——— DUTY: nghĩa vụ đang treo. Bậc này CAO HƠN SETUP — người đang nợ không cần nghe lời khuyên dựng nhóm.
  {
    id: "DUTY-MY-UNPAID",
    triggers: ["OPEN", "ASKED_NEXT", "TASK_DONE", "RETURN_AFTER_ABSENCE"],
    roles: ["owner", "admin", "treasurer", "member"],
    when: [{ signal: "myUnpaidDueCount", op: "gt", value: 0 }],
    band: "DUTY",
    priority: 90,
    // 🔴 US-AIV2-0112 H05 (@UC0322): `{chiTietKhoan}` = tên khoản · số tiền còn thiếu · hạn (fact vắng ⇒ "" ⇒ câu cũ nguyên văn).
    /*
     * 🔴 US-AIV2-tourguide-0004 (@UC0360 · Q1 · K15 · K10 · Q3) — câu MẶC ĐỊNH (có nút, STK/cách vào sổ CHƯA BIẾT) không khẳng định có chỗ
     * chuyển khoản lẫn cách khoản vào sổ. Biến thể dưới nói đúng khi đã biết: nhóm chưa có STK (Q1) · lời tự khai VÀO SỔ NGAY hay CHỜ ĐỐI
     * CHIẾU (vị-từ của cửa — `myReportAutoConfirm`) · không nút ⇒ không mời báo (K10: quỹ chỉ người giữ quỹ ghi). Đếm = "khoản của tôi" (mọi
     * đợt, kể cả đợt đã đóng sổ — K16/W6.dat).
     */
    templateVi: "Bạn còn {soViec} khoản cần đóng{chiTietKhoan}. Đóng xong thì bạn báo lại để nhóm biết nhé.",
    vars: { soViec: "myUnpaidDueCount" },
    cta: { page: "PAY-REPORT", label: "Báo tôi đã nộp tiền" },
    claims: [
      { clause: "Bạn còn {soViec} khoản cần đóng{chiTietKhoan}", proof: "myUnpaidDueCount" },
      { clause: "bạn báo lại", proof: "door" },
    ],
    variants: [
      {
        id: "DUTY-MY-UNPAID/q1-vao-so-ngay",
        outcomes: ["ready"],
        when: [
          { signal: "bankAccountMissing", op: "truthy" },
          { signal: "myReportAutoConfirm", op: "eq", value: "AUTO" },
        ],
        templateVi: "Bạn còn {soViec} khoản cần đóng{chiTietKhoan}. Nhóm chưa có tài khoản nhận tiền, nên bạn cứ đóng theo cách cả nhóm đã thống nhất. Xong rồi bạn báo đã chuyển là khoản vào sổ ngay.",
        withCard: { card: GUIDE_BANK_CARD_ID, templateVi: "Bạn còn {soViec} khoản cần đóng{chiTietKhoan}. Đóng xong, bạn báo đã chuyển là khoản vào sổ ngay." },
        // US-AIV2-tourguide-0006 (nợ "khoản của tôi"): câu trả lời của trang đã nói số khoản + cách báo ⇒ chỉ còn vế nhóm CHƯA có tài khoản.
        whenCovered: "Nhóm chưa có tài khoản nhận tiền, nên bạn cứ đóng theo cách cả nhóm đã thống nhất.",
        claims: [
          { clause: "Bạn còn {soViec} khoản cần đóng{chiTietKhoan}", proof: "myUnpaidDueCount" },
          { clause: "Nhóm chưa có tài khoản nhận tiền", proof: "bankAccountMissing" },
          { clause: "bạn báo đã chuyển", proof: "door" },
          { clause: "khoản vào sổ ngay", proof: "myReportAutoConfirm" },
        ],
      },
      {
        id: "DUTY-MY-UNPAID/q1-cho-doi-chieu",
        outcomes: ["ready"],
        // 🔴 US-AIV2-tourguide-0014 (@UC0377 · N3): "để người giữ quỹ đối chiếu" CHỈ với thành viên. Chủ nhóm/quản trị/thủ quỹ nhận REVIEW chỉ khi quỹ
        // ĐÓNG BĂNG (vế vai của `selfReportAutoConfirms` luôn đúng) — lúc đó không ai đối chiếu được; họ + vai không biết ⇒ `…/q1` (câu đã duyệt, không vai).
        when: [
          { signal: "bankAccountMissing", op: "truthy" },
          { signal: "myReportAutoConfirm", op: "eq", value: "REVIEW" },
          { signal: "role", op: "eq", value: "member" },
        ],
        templateVi: "Bạn còn {soViec} khoản cần đóng{chiTietKhoan}. Nhóm chưa có tài khoản nhận tiền, nên bạn cứ đóng theo cách cả nhóm đã thống nhất. Xong rồi bạn báo đã chuyển để người giữ quỹ đối chiếu.",
        whenCovered: "Nhóm chưa có tài khoản nhận tiền, nên bạn cứ đóng theo cách cả nhóm đã thống nhất.",
        claims: [
          { clause: "Bạn còn {soViec} khoản cần đóng{chiTietKhoan}", proof: "myUnpaidDueCount" },
          { clause: "Nhóm chưa có tài khoản nhận tiền", proof: "bankAccountMissing" },
          { clause: "bạn báo đã chuyển", proof: "door" },
          { clause: "để người giữ quỹ đối chiếu", proof: "myReportAutoConfirm" },
        ],
      },
      {
        id: "DUTY-MY-UNPAID/q1",
        outcomes: ["ready"],
        when: [{ signal: "bankAccountMissing", op: "truthy" }],
        templateVi: "Bạn còn {soViec} khoản cần đóng{chiTietKhoan}. Nhóm chưa có tài khoản nhận tiền, nên bạn cứ đóng theo cách cả nhóm đã thống nhất. Xong rồi bạn báo đã chuyển nhé.",
        withCard: { card: GUIDE_BANK_CARD_ID, templateVi: "Bạn còn {soViec} khoản cần đóng{chiTietKhoan}. Đóng xong, bạn báo đã chuyển nhé." },
        whenCovered: "Nhóm chưa có tài khoản nhận tiền, nên bạn cứ đóng theo cách cả nhóm đã thống nhất.",
        claims: [
          { clause: "Bạn còn {soViec} khoản cần đóng{chiTietKhoan}", proof: "myUnpaidDueCount" },
          { clause: "Nhóm chưa có tài khoản nhận tiền", proof: "bankAccountMissing" },
          { clause: "bạn báo đã chuyển", proof: "door" },
        ],
        note: "Q1 — câu nháp của spec §2; cách vào sổ trộn/không biết ⇒ không khẳng định. 0014: cũng là câu của chủ nhóm/quản trị/thủ quỹ khi lời khai chờ (quỹ đóng băng).",
      },
      {
        id: "DUTY-MY-UNPAID/vao-so-ngay",
        outcomes: ["ready"],
        when: [
          { signal: "bankAccountMissing", op: "falsy" },
          { signal: "myReportAutoConfirm", op: "eq", value: "AUTO" },
        ],
        templateVi: "Bạn còn {soViec} khoản cần đóng{chiTietKhoan}. Chuyển xong, bạn báo đã chuyển là khoản vào sổ ngay.",
        claims: [
          { clause: "Bạn còn {soViec} khoản cần đóng{chiTietKhoan}", proof: "myUnpaidDueCount" },
          { clause: "Chuyển xong", proof: "bankAccountMissing" },
          { clause: "báo đã chuyển là khoản vào sổ ngay", proof: "myReportAutoConfirm" },
        ],
        note: "K15 — người khai là chủ nhóm/quản trị/thủ quỹ, hoặc quỹ bật «tự khai là vào sổ».",
      },
      {
        id: "DUTY-MY-UNPAID/cho-doi-chieu",
        outcomes: ["ready"],
        // US-AIV2-tourguide-0014 (@UC0377 · N3): như `q1-cho-doi-chieu` — chỉ thành viên; O/A/T + vai không biết ⇒ `…/co-stk` (câu đã duyệt, không vai).
        when: [
          { signal: "bankAccountMissing", op: "falsy" },
          { signal: "myReportAutoConfirm", op: "eq", value: "REVIEW" },
          { signal: "role", op: "eq", value: "member" },
        ],
        templateVi: "Bạn còn {soViec} khoản cần đóng{chiTietKhoan}. Chuyển xong, bạn báo lại để người giữ quỹ đối chiếu nhé.",
        claims: [
          { clause: "Bạn còn {soViec} khoản cần đóng{chiTietKhoan}", proof: "myUnpaidDueCount" },
          { clause: "Chuyển xong", proof: "bankAccountMissing" },
          { clause: "để người giữ quỹ đối chiếu", proof: "myReportAutoConfirm" },
        ],
        note: "Câu cũ — GIỮ NGUYÊN cho người mà lời báo chờ đối chiếu (AC2).",
      },
      {
        id: "DUTY-MY-UNPAID/co-stk",
        outcomes: ["ready"],
        when: [{ signal: "bankAccountMissing", op: "falsy" }],
        templateVi: "Bạn còn {soViec} khoản cần đóng{chiTietKhoan}. Chuyển xong, bạn báo lại để nhóm biết nhé.",
        claims: [
          { clause: "Bạn còn {soViec} khoản cần đóng{chiTietKhoan}", proof: "myUnpaidDueCount" },
          { clause: "Chuyển xong", proof: "bankAccountMissing" },
        ],
        note: "Khoản ở các quỹ cấu hình khác nhau (trộn) ⇒ không khẳng định cách vào sổ (L-22). 0014: cũng là câu của chủ nhóm/quản trị/thủ quỹ khi lời khai chờ (quỹ đóng băng) — không nêu người đối chiếu.",
      },
      {
        id: "DUTY-MY-UNPAID/q1-khong-tu-bao",
        outcomes: ["NOT_SELF_REPORTABLE"],
        // US-AIV2-tourguide-0013 (@UC0377 · K04-họ): "người giữ quỹ sẽ ghi nhận" CHỈ với thành viên — chủ nhóm/quản trị/thủ quỹ CHÍNH là người ghi nhận
        // (RECORD-PAID); vai khác / vai không biết ⇒ biến thể `…/quan-ly` dưới (bỏ vế người khác ghi).
        when: [{ signal: "bankAccountMissing", op: "truthy" }, { signal: "role", op: "eq", value: "member" }],
        templateVi: "Bạn còn {soViec} khoản cần đóng{chiTietKhoan}. Nhóm chưa có tài khoản nhận tiền — người giữ quỹ sẽ ghi nhận khi nhận được tiền.",
        claims: [
          { clause: "Bạn còn {soViec} khoản cần đóng{chiTietKhoan}", proof: "myUnpaidDueCount" },
          { clause: "Nhóm chưa có tài khoản nhận tiền", proof: "bankAccountMissing" },
          { clause: "người giữ quỹ sẽ ghi nhận khi nhận được tiền", proof: "door" },
        ],
      },
      {
        id: "DUTY-MY-UNPAID/khong-tu-bao",
        outcomes: ["NOT_SELF_REPORTABLE"],
        when: [{ signal: "role", op: "eq", value: "member" }],
        templateVi: "Bạn còn {soViec} khoản cần đóng{chiTietKhoan}. Người giữ quỹ sẽ ghi nhận khi nhận được tiền.",
        claims: [
          { clause: "Bạn còn {soViec} khoản cần đóng{chiTietKhoan}", proof: "myUnpaidDueCount" },
          { clause: "Người giữ quỹ sẽ ghi nhận khi nhận được tiền", proof: "door" },
        ],
        note: "K10 — quỹ chỉ người giữ quỹ ghi (admin_one_party) / khoản ở đợt đã đóng sổ: không mời báo. QĐ PO 04/10 dòng 42: nghĩa chung, CHỈ khi còn nợ mà không tự báo được.",
      },
      /**
       * 🔴 US-AIV2-tourguide-0013 (@UC0377 · K04-họ) — còn nợ mà không tự báo được, người đọc KHÔNG phải thành viên thường (chủ nhóm/quản trị/thủ quỹ — chính
       * họ ghi nhận được) hoặc vai không biết: bỏ vế "người giữ quỹ sẽ ghi nhận" (câu GHÉP — đúng nguyên văn `q1-khong-nut` / `khong-nut`). Đo 05/10:
       * `W6.hung` (quản trị, quỹ chỉ người quản lý ghi) nghe "Người giữ quỹ sẽ ghi nhận…" 5 ô.
       */
      {
        id: "DUTY-MY-UNPAID/q1-khong-tu-bao-quan-ly",
        outcomes: ["NOT_SELF_REPORTABLE"],
        when: [{ signal: "bankAccountMissing", op: "truthy" }],
        templateVi: "Bạn còn {soViec} khoản cần đóng{chiTietKhoan}. Nhóm chưa có tài khoản nhận tiền.",
        withCard: { card: GUIDE_BANK_CARD_ID, templateVi: "Bạn còn {soViec} khoản cần đóng{chiTietKhoan}." },
        claims: [
          { clause: "Bạn còn {soViec} khoản cần đóng{chiTietKhoan}", proof: "myUnpaidDueCount" },
          { clause: "Nhóm chưa có tài khoản nhận tiền", proof: "bankAccountMissing" },
        ],
      },
      {
        id: "DUTY-MY-UNPAID/khong-tu-bao-quan-ly",
        outcomes: ["NOT_SELF_REPORTABLE"],
        templateVi: "Bạn còn {soViec} khoản cần đóng{chiTietKhoan}.",
        claims: [{ clause: "Bạn còn {soViec} khoản cần đóng{chiTietKhoan}", proof: "myUnpaidDueCount" }],
      },
      /**
       * 🔴 G-4 GUIDE-FROZEN-FUND (@UC0361 @UC9300 · nợ 0014 khảo sát (1)(2)) — mọi khoản tự báo được nằm ở quỹ ĐÓNG BĂNG: cửa của trợ lý không mở
       * (ô khoản rỗng `SCOPE_FUND_FROZEN`), không ai đối chiếu được ⇒ không nút, không «người giữ quỹ đối chiếu». Vế sau GHÉP câu đã duyệt của mã
       * `FUND_FROZEN` (PO-DUYET 0014 dòng 4). Đo B0 15/10: `tu` (W1 khoá) nghe «…báo lại để người giữ quỹ đối chiếu» + nút ⇒ «Quỹ đang bị đóng băng…».
       */
      {
        id: "DUTY-MY-UNPAID/quy-khoa",
        outcomes: ["FUND_FROZEN"],
        templateVi: "Bạn còn {soViec} khoản cần đóng{chiTietKhoan}. Quỹ đang tạm khoá nên chưa ghi thêm được.",
        claims: [
          { clause: "Bạn còn {soViec} khoản cần đóng{chiTietKhoan}", proof: "myUnpaidDueCount" },
          { clause: "Quỹ đang tạm khoá nên chưa ghi thêm được", proof: "door" },
        ],
        note: "G-4 — khoản tự báo được chỉ còn ở quỹ đóng băng (không đường mở băng trong app): nói sự thật, không mời, không nêu người đối chiếu.",
      },
      {
        id: "DUTY-MY-UNPAID/q1-khong-nut",
        outcomes: ["unknown", "SUBJECT_GONE", "NOTHING_TO_REPORT"],
        when: [{ signal: "bankAccountMissing", op: "truthy" }],
        templateVi: "Bạn còn {soViec} khoản cần đóng{chiTietKhoan}. Nhóm chưa có tài khoản nhận tiền.",
        withCard: { card: GUIDE_BANK_CARD_ID, templateVi: "Bạn còn {soViec} khoản cần đóng{chiTietKhoan}." },
        claims: [
          { clause: "Bạn còn {soViec} khoản cần đóng{chiTietKhoan}", proof: "myUnpaidDueCount" },
          { clause: "Nhóm chưa có tài khoản nhận tiền", proof: "bankAccountMissing" },
        ],
      },
      {
        id: "DUTY-MY-UNPAID/khong-nut",
        outcomes: ["unknown", "SUBJECT_GONE", "NOTHING_TO_REPORT"],
        templateVi: "Bạn còn {soViec} khoản cần đóng{chiTietKhoan}.",
        claims: [{ clause: "Bạn còn {soViec} khoản cần đóng{chiTietKhoan}", proof: "myUnpaidDueCount" }],
      },
      {
        id: "DUTY-MY-UNPAID/im",
        outcomes: ["ALREADY_REPORTED", "NOTHING_OWED"],
        silent: true,
        note: "QĐ PO 04/10 dòng 42: cửa tự khai vừa nói người này KHÔNG còn khoản phải đóng (đã báo / đã đóng) — phủ định câu thẻ ⇒ thẻ im ở lượt này.",
      },
    ],
    /*
     * 🔴 W-5 UX-MANAGER-SELF-PAY (@UC0377 · N-5 · CLAUDE.md §3.7) — còn nợ mà KHÔNG tự báo được (`NOT_SELF_REPORTABLE`: khoản đợt mở, quỹ không đóng băng,
     * không tự khai được ⇒ quỹ chỉ nhận ghi nhận `admin_one_party`), người đọc là chủ nhóm/quản trị/thủ quỹ: tự «Ghi nhận đã đóng» CHÍNH khoản câu nêu
     * (`RECORD-PAID` — O/A/T ghi được mọi khoản, kể cả của mình; QĐ 29/08 `record-paid.service.ts`). Cổng trang + `RECORD-PAID.ready(khoản)` quyết: thành
     * viên thường không có trang đó ⇒ không lối (không nới gì) · khoản NỢ QUỸ của chính mình khi nhóm còn quản lý thật khác ⇒ `ready` từ chối (§3.7
     * ngoại lệ ngược — chờ O/A/T khác) ⇒ câu không nút `…/khong-tu-bao-quan-ly` như cũ.
     * ⚠️ Mệnh đề «quỹ này không nhận lời tự báo» (`proof: "door"`) đứng trên HAI cửa (review W-5 MED-1): `NOT_SELF_REPORTABLE` = còn nợ mà 0 khoản ĐỢT MỞ tự báo
     * được (gồm cả khoản treo ở đợt đã đóng sổ), VÀ `RECORD-PAID.ready(khoản)` chỉ cho khoản ĐỢT MỞ · quỹ không khoá (đợt đóng ⇒ `SUBJECT_GONE`, khoá ở
     * `guide-ready.binding.spec.ts`). Khoản câu nêu ghi nhận được ở đợt mở mà không tự báo được ⇒ chỉ còn lý do chế độ quỹ (`admin_one_party`). Nới một
     * trong hai cửa (vd `ready` nhận đợt đóng) là phải sửa câu này.
     * Nợ có địa chỉ (review W-5 MED-2): lối trỏ `myNextDue` (khoản câu nêu) — khoản đó là nợ quỹ `self_blocked` mà còn khoản khác ghi nhận được ⇒ không
     * nút (thiếu nút, không thừa nút); người quản lý vẫn có nút danh mục «Ghi nhận đã đóng».
     */
    doors: [
      {
        id: "DUTY-MY-UNPAID/tu-ghi-nhan-q1",
        outcomes: ["NOT_SELF_REPORTABLE"],
        when: [{ signal: "bankAccountMissing", op: "truthy" }],
        cta: { page: "RECORD-PAID", label: "Ghi nhận đã đóng" },
        ctaSubject: "myNextDue",
        templateVi:
          "Bạn còn {soViec} khoản cần đóng{chiTietKhoan}. Nhóm chưa có tài khoản nhận tiền, và quỹ này không nhận lời tự báo. Bạn là người quản lý, nên khi quỹ đã nhận tiền thì bạn tự ghi nhận đã đóng cho khoản này nhé.",
        claims: [
          { clause: "Bạn còn {soViec} khoản cần đóng{chiTietKhoan}", proof: "myUnpaidDueCount" },
          { clause: "Nhóm chưa có tài khoản nhận tiền", proof: "bankAccountMissing" },
          { clause: "quỹ này không nhận lời tự báo", proof: "door" },
          { clause: "Bạn là người quản lý", proof: "door" },
          { clause: "bạn tự ghi nhận đã đóng cho khoản này", proof: "door" },
        ],
      },
      {
        id: "DUTY-MY-UNPAID/tu-ghi-nhan",
        outcomes: ["NOT_SELF_REPORTABLE"],
        cta: { page: "RECORD-PAID", label: "Ghi nhận đã đóng" },
        ctaSubject: "myNextDue",
        templateVi: "Bạn còn {soViec} khoản cần đóng{chiTietKhoan}. Quỹ này không nhận lời tự báo. Bạn là người quản lý, nên khi quỹ đã nhận tiền thì bạn tự ghi nhận đã đóng cho khoản này nhé.",
        claims: [
          { clause: "Bạn còn {soViec} khoản cần đóng{chiTietKhoan}", proof: "myUnpaidDueCount" },
          { clause: "Quỹ này không nhận lời tự báo", proof: "door" },
          { clause: "Bạn là người quản lý", proof: "door" },
          { clause: "bạn tự ghi nhận đã đóng cho khoản này", proof: "door" },
        ],
        note: "N-5 — quỹ chỉ nhận ghi nhận của người quản lý: người quản lý còn nợ tự ghi nhận CHÍNH khoản câu nêu (nút mang đúng khoản đó).",
      },
    ],
  },
  {
    id: "DUTY-CONFIRM-PENDING",
    triggers: ["OPEN", "ASKED_NEXT", "TASK_DONE", "RETURN_AFTER_ABSENCE"],
    roles: ["owner", "admin", "treasurer"],
    when: [{ signal: "pendingConfirmations", op: "gt", value: 0 }],
    band: "DUTY",
    priority: 85,
    // US-AIV2-tourguide-0004: số = ĐÚNG danh sách cửa duyệt trả cho người này (`listPendingConfirmationsForUser`); nhãn nói đúng việc GHI
    // của trang (duyệt / từ chối) — "Xem lời báo" hứa một việc đọc.
    // review 0004 M2: số đếm CHỈ lời báo duyệt xong là VÀO SỔ (quỹ không đóng băng, nhóm không đình chỉ/lưu trữ) — danh sách cửa duyệt có thể
    // dài hơn ⇒ câu nói "đối chiếu được ngay", không nói đó là TẤT CẢ lời báo đang chờ.
    templateVi: "Có {soViec} lời báo đã chuyển bạn đối chiếu được ngay. Khoản nào bạn xác nhận thì mới vào sổ.",
    vars: { soViec: "pendingConfirmations" },
    cta: { page: "PAY-CONFIRM-DECIDE", label: "Duyệt báo đã nộp tiền" },
    claims: [
      { clause: "Có {soViec} lời báo đã chuyển bạn đối chiếu được ngay", proof: "pendingConfirmations" },
      { clause: "Khoản nào bạn xác nhận thì mới vào sổ", proof: "door" },
    ],
    variants: [
      {
        id: "DUTY-CONFIRM-PENDING/tren-man-hinh",
        outcomes: ["CLAIM_NEEDS_SCREEN"],
        templateVi: "Có {soViec} lời báo đã chuyển đang chờ bạn đối chiếu. Có người báo nhiều khoản cùng lúc, nên bạn đối chiếu từng khoản ở tab Hoạt động của nhóm nhé.",
        claims: [
          { clause: "Có {soViec} lời báo đã chuyển đang chờ bạn đối chiếu", proof: "pendingConfirmations" },
          { clause: "Có người báo nhiều khoản cùng lúc", proof: "door" },
        ],
        note: "Cùng ý câu lý do CLAIM_NEEDS_SCREEN của catalog lúc bấm.",
      },
      { id: "DUTY-CONFIRM-PENDING/im", outcomes: ["NOTHING_TO_CONFIRM"], silent: true, note: "Cửa duyệt vừa trả 0 lời báo — phủ định chính câu thẻ ⇒ thẻ im ở lượt này." },
      {
        id: "DUTY-CONFIRM-PENDING/khong-nut",
        // G-4: `FUND_FROZEN` chỉ ra khi lời báo vừa bị khoá giữa phép đếm và `ready()` (phép đếm đã loại quỹ đóng băng) — câu không nút vẫn đúng.
        outcomes: ["unknown", "SUBJECT_GONE", "FUND_FROZEN"],
        templateVi: "Có {soViec} lời báo đã chuyển đang chờ bạn đối chiếu.",
        claims: [{ clause: "Có {soViec} lời báo đã chuyển đang chờ bạn đối chiếu", proof: "pendingConfirmations" }],
      },
    ],
  },
  /**
   * 🔴 AI-SO-DRIFT-1012 (@UC0217 @UC0035 · QĐ người dùng 12/10/2026) — YÊU CẦU CHI chờ duyệt. Thành viên gửi yêu cầu chi ở màn nhóm (nút «Chi
   * quỹ»); không ai nhắc người duyệt thì yêu cầu nằm im. Số = khoản DUYỆT THẲNG được (`ExpenseApprovalQueue` — CÙNG hàm `ready()` của EXP-APPROVE
   * đọc): khoản phải biểu quyết · quỹ đang khoá · người ứng là chính mình KHÔNG đếm (mời "Duyệt" cho chúng là ngõ cụt 403/409). Nêu TÊN khoản chi
   * (như `DUTY-OPEN-VOTE`), không tên người gửi. Vai = vai trang EXP-APPROVE (O/A/T — tín hiệu không nạp cho thành viên).
   */
  {
    id: "DUTY-EXPENSE-REQUESTS",
    triggers: ["OPEN", "ASKED_NEXT", "TASK_DONE", "RETURN_AFTER_ABSENCE"],
    roles: ["owner", "admin", "treasurer"],
    when: [{ signal: "pendingExpenseApprovals", op: "gt", value: 0 }],
    band: "DUTY",
    priority: 80,
    templateVi: "Có {soKhoanChiDuyet} khoản chi đang chờ bạn duyệt: {tenKhoanChiDuyet}.",
    vars: { soKhoanChiDuyet: "pendingExpenseApprovals" },
    cta: { page: "EXP-APPROVE", label: "Duyệt khoản chi" },
    claims: [{ clause: "Có {soKhoanChiDuyet} khoản chi đang chờ bạn duyệt: {tenKhoanChiDuyet}", proof: "pendingExpenseApprovals" }],
    variants: [
      {
        id: "DUTY-EXPENSE-REQUESTS/mot",
        outcomes: ["ready"],
        when: [{ signal: "pendingExpenseApprovals", op: "eq", value: 1 }],
        templateVi: "Khoản chi {tenKhoanChiDuyet} đang chờ bạn duyệt.",
        claims: [{ clause: "Khoản chi {tenKhoanChiDuyet} đang chờ bạn duyệt", proof: "pendingExpenseApprovals" }],
      },
      {
        id: "DUTY-EXPENSE-REQUESTS/im",
        outcomes: ["NOTHING_TO_DECIDE"],
        silent: true,
        note: "Danh sách khoản chờ người này quyết vừa rỗng (người khác vừa duyệt/từ chối, người gửi vừa huỷ) — phủ định câu thẻ ⇒ thẻ im ở lượt này.",
      },
      {
        id: "DUTY-EXPENSE-REQUESTS/mot-khong-nut",
        outcomes: ["unknown"],
        when: [{ signal: "pendingExpenseApprovals", op: "eq", value: 1 }],
        templateVi: "Khoản chi {tenKhoanChiDuyet} đang chờ bạn duyệt.",
        claims: [{ clause: "Khoản chi {tenKhoanChiDuyet} đang chờ bạn duyệt", proof: "pendingExpenseApprovals" }],
      },
      {
        id: "DUTY-EXPENSE-REQUESTS/khong-nut",
        outcomes: ["unknown"],
        // Biến thể không điều kiện (lưới vét cạn: luôn nói được) ⇒ chỉ chỗ trống số, không tên khoản (khuôn DUTY-PAY-ON-BEHALF/khong-nut).
        templateVi: "Có {soKhoanChiDuyet} khoản chi đang chờ bạn duyệt.",
        claims: [{ clause: "Có {soKhoanChiDuyet} khoản chi đang chờ bạn duyệt", proof: "pendingExpenseApprovals" }],
      },
    ],
  },
  {
    id: "DUTY-MY-CLAIMED",
    triggers: ["OPEN", "ASKED_NEXT", "TASK_DONE", "RETURN_AFTER_ABSENCE"],
    roles: ["owner", "admin", "treasurer", "member"],
    when: [{ signal: "myClaimedDueCount", op: "gt", value: 0 }],
    band: "DUTY",
    priority: 50,
    // "Đã chuyển – chưa đối chiếu" là trạng thái TRUNG THỰC THỨ BA (§3.7): máy không bao giờ tự nói "đã nhận".
    // US-AIV2-tourguide-0013 (@UC0377 · K04-họ): câu mặc định KHÔNG nêu người đối chiếu — chủ nhóm/quản trị/thủ quỹ có lời báo chờ (báo lúc còn là
    // thành viên, hoặc quỹ đóng băng) CHÍNH là người đối chiếu được (không chặn tự duyệt). "người giữ quỹ" chỉ với thành viên (biến thể dưới). Đo 05/10:
    // `W2.quan` · `W3.phong` (quản trị) nghe "đang chờ người giữ quỹ đối chiếu" 8 ô.
    templateVi: "Bạn đã báo chuyển {soViec} khoản, đang chờ đối chiếu.",
    vars: { soViec: "myClaimedDueCount" },
    ctaless: "waiting_on_others",
    claims: [{ clause: "Bạn đã báo chuyển {soViec} khoản, đang chờ đối chiếu", proof: "myClaimedDueCount" }],
    variants: [
      {
        // 🔴 G-4 GUIDE-FROZEN-FUND (@UC0360 @UC0207 · 0014 khảo sát (2) cùng họ): MỌI lời báo đang chờ của tôi ở quỹ ĐÓNG BĂNG ⇒ không ai đối
        // chiếu được (duyệt 409, không đường mở băng) ⇒ không bảo chờ ai. GHÉP vế đã duyệt «chưa đối chiếu được vì quỹ đang tạm khoá» (PO-DUYET 0014
        // dòng 9, câu "sao chưa xác nhận"). Trộn với quỹ mở ⇒ câu cũ (đang chờ đối chiếu là thật với khoản ở quỹ mở).
        id: "DUTY-MY-CLAIMED/quy-khoa",
        when: [{ signal: "myClaimedAllFrozen", op: "truthy" }],
        templateVi: "Bạn đã báo chuyển {soViec} khoản, chưa đối chiếu được vì quỹ đang tạm khoá.",
        claims: [
          { clause: "Bạn đã báo chuyển {soViec} khoản", proof: "myClaimedDueCount" },
          { clause: "chưa đối chiếu được vì quỹ đang tạm khoá", proof: "myClaimedAllFrozen" },
        ],
      },
      {
        id: "DUTY-MY-CLAIMED/thanh-vien",
        when: [{ signal: "role", op: "eq", value: "member" }],
        templateVi: "Bạn đã báo chuyển {soViec} khoản, đang chờ người giữ quỹ đối chiếu.",
        claims: [{ clause: "Bạn đã báo chuyển {soViec} khoản, đang chờ người giữ quỹ đối chiếu", proof: "myClaimedDueCount" }],
      },
    ],
  },
  /**
   * 🔴🔴 US-AIV2-tourguide-0011 (@UC0371 · QĐ PO D3) — LỜI ĐỀ NGHỊ ĐÓNG HỘ đang chờ CHÍNH người này trả lời (người khác đề nghị đóng khoản của
   * mình). Trước: chỉ nhánh `myPending` của `READ-PEOPLE-MINE` kể ("nộp hộ: X") — một bộ máy thứ hai, lời chào không biết. Nay là thẻ ⇒ lời chào
   * và "làm gì tiếp" nói cùng một câu. Nguồn = cửa «Việc của tôi» (`payOnBehalf`, đã lọc suất không còn nhận nộp hộ — H1 #1177).
   * Nút `PAY-ON-BEHALF` (trang trả lời đồng ý/từ chối) qua cổng `0003`. Câu nói HẬU QUẢ TIỀN của «Đồng ý» (§3.7 + `pay-on-behalf.ts`: chấp nhận
   * mới ghi sổ; người được đóng hộ nợ lại người đóng CHỈ khi quỹ ghi nợ — QĐ PO 04/10, biến thể dưới) — không nói tên người đề nghị (cửa không
   * trả, và câu không cần).
   */
  {
    id: "DUTY-PAY-ON-BEHALF",
    triggers: ["OPEN", "ASKED_NEXT", "TASK_DONE", "RETURN_AFTER_ABSENCE"],
    roles: ["owner", "admin", "treasurer", "member"],
    when: [{ signal: "myPayOnBehalfCount", op: "gt", value: 0 }],
    band: "DUTY",
    priority: 80,
    /*
     * 🔴 QĐ PO 04/10/2026 (review #1215 LOW-2 — «Hai biến thể theo cài đặt quỹ»): câu MẶC ĐỊNH không có vế nợ. Vế «và bạn nợ lại người đóng
     * hộ» chỉ ở biến thể khi `myPayOnBehalfCreatesDebt` ĐÃ BIẾT là true (mọi lời đề nghị ở quỹ ghi nợ — cửa `_respondInTx` chỉ ghi `DebtEntry`
     * khi resolver của quỹ trả true). `false` (nộp hộ = tặng) · KHÔNG BIẾT · quỹ trộn ⇒ biến thể không khớp ⇒ câu không vế nợ (không đoán).
     */
    templateVi: "Có {soDeNghi} lời đề nghị đóng hộ khoản của bạn đang chờ bạn trả lời: {tenKhoanNopHo}. Bạn đồng ý thì khoản đó được tính là đã đóng.",
    vars: { soDeNghi: "myPayOnBehalfCount" },
    cta: { page: "PAY-ON-BEHALF", label: "Trả lời đề nghị đóng hộ" },
    ctaSubject: "myPayOnBehalf",
    claims: [
      { clause: "Có {soDeNghi} lời đề nghị đóng hộ khoản của bạn đang chờ bạn trả lời: {tenKhoanNopHo}", proof: "myPayOnBehalfCount" },
      { clause: "Bạn đồng ý thì khoản đó được tính là đã đóng", proof: "door" },
    ],
    variants: [
      {
        id: "DUTY-PAY-ON-BEHALF/mot",
        outcomes: ["ready"],
        when: [
          { signal: "myPayOnBehalfCount", op: "eq", value: 1 },
          { signal: "myPayOnBehalfCreatesDebt", op: "truthy" },
        ],
        templateVi: "Có người đề nghị đóng hộ khoản của bạn ({tenKhoanNopHo}), đang chờ bạn trả lời. Bạn đồng ý thì khoản đó được tính là đã đóng, và bạn nợ lại người đóng hộ.",
        claims: [
          { clause: "Có người đề nghị đóng hộ khoản của bạn ({tenKhoanNopHo}), đang chờ bạn trả lời", proof: "myPayOnBehalfCount" },
          { clause: "Bạn đồng ý thì khoản đó được tính là đã đóng", proof: "door" },
          { clause: "và bạn nợ lại người đóng hộ", proof: "myPayOnBehalfCreatesDebt" },
        ],
        note: "QĐ PO 04/10 — quỹ đặt «đồng ý thì bạn nợ lại người đóng hộ» (`pay_on_behalf_creates_debt` = true, ĐÃ BIẾT).",
      },
      {
        id: "DUTY-PAY-ON-BEHALF/mot-tang",
        outcomes: ["ready"],
        when: [{ signal: "myPayOnBehalfCount", op: "eq", value: 1 }],
        templateVi: "Có người đề nghị đóng hộ khoản của bạn ({tenKhoanNopHo}), đang chờ bạn trả lời. Bạn đồng ý thì khoản đó được tính là đã đóng.",
        claims: [
          { clause: "Có người đề nghị đóng hộ khoản của bạn ({tenKhoanNopHo}), đang chờ bạn trả lời", proof: "myPayOnBehalfCount" },
          { clause: "Bạn đồng ý thì khoản đó được tính là đã đóng", proof: "door" },
        ],
        note: "QĐ PO 04/10 — quỹ coi nộp hộ là tặng (false) hoặc cài đặt KHÔNG BIẾT ⇒ không nói vế nợ.",
      },
      {
        id: "DUTY-PAY-ON-BEHALF/no-lai",
        outcomes: ["ready"],
        when: [{ signal: "myPayOnBehalfCreatesDebt", op: "truthy" }],
        templateVi: "Có {soDeNghi} lời đề nghị đóng hộ khoản của bạn đang chờ bạn trả lời: {tenKhoanNopHo}. Bạn đồng ý thì khoản đó được tính là đã đóng, và bạn nợ lại người đóng hộ.",
        claims: [
          { clause: "Có {soDeNghi} lời đề nghị đóng hộ khoản của bạn đang chờ bạn trả lời: {tenKhoanNopHo}", proof: "myPayOnBehalfCount" },
          { clause: "Bạn đồng ý thì khoản đó được tính là đã đóng", proof: "door" },
          { clause: "và bạn nợ lại người đóng hộ", proof: "myPayOnBehalfCreatesDebt" },
        ],
        note: "QĐ PO 04/10 — ≥2 lời đề nghị, quỹ của MỌI lời đề nghị đặt «bạn nợ lại người đóng hộ» (quỹ trộn ⇒ câu mặc định, không vế nợ).",
      },
      {
        id: "DUTY-PAY-ON-BEHALF/im",
        // review #1437 LOW-3: `FUND_FROZEN` chỉ ra khi quỹ vừa khoá giữa phép đếm (đã loại quỹ khoá) và `ready()` — câu «đang chờ bạn trả lời» không còn đúng ⇒ im.
        // AI-MENU-POB-ENTRY (@UC0218): `NOTHING_TO_PAY_ON_BEHALF` (nút không đối tượng — ≥2 lời đề nghị) = cửa vừa nói không còn lời nào chờ ⇒ phủ định câu thẻ ⇒ im.
        outcomes: ["SUBJECT_GONE", "FUND_FROZEN", "NOTHING_TO_PAY_ON_BEHALF"],
        silent: true,
        note: "review #1215 MEDIUM-2: cửa vừa nói lời đề nghị đó không còn (đã trả lời/huỷ) — phủ định câu thẻ ⇒ thẻ im ở lượt này (khuôn DUTY-CONFIRM-PENDING/im).",
      },
      {
        id: "DUTY-PAY-ON-BEHALF/mot-khong-nut",
        outcomes: ["unknown"],
        when: [{ signal: "myPayOnBehalfCount", op: "eq", value: 1 }],
        templateVi: "Có người đề nghị đóng hộ khoản của bạn ({tenKhoanNopHo}), đang chờ bạn trả lời.",
        claims: [{ clause: "Có người đề nghị đóng hộ khoản của bạn ({tenKhoanNopHo}), đang chờ bạn trả lời", proof: "myPayOnBehalfCount" }],
      },
      {
        id: "DUTY-PAY-ON-BEHALF/khong-nut",
        outcomes: ["unknown"],
        // Biến thể không điều kiện (lưới vét cạn: luôn nói được) ⇒ chỉ chỗ trống số, không tên khoản.
        templateVi: "Có {soDeNghi} lời đề nghị đóng hộ khoản của bạn đang chờ bạn trả lời.",
        claims: [{ clause: "Có {soDeNghi} lời đề nghị đóng hộ khoản của bạn đang chờ bạn trả lời", proof: "myPayOnBehalfCount" }],
      },
    ],
  },
  /**
   * 🔴🔴 US-AIV2-tourguide-0011 (@UC0371 · QĐ PO D3 · review v2 N2) — KHOẢN CHI đang chờ CHÍNH người này bỏ phiếu (cửa «Việc của tôi»: phiên mở
   * mà lá phiếu của người này còn suất chưa bỏ — `ActionItemsService.resolveOpenVotes`). Câu nêu TÊN khoản chi.
   * 🔴 KHÔNG nút: trang `EXP-VOTE` là `redirect` tới trang nhóm — chip redirect không mang id khoản chi nên không mở ĐÚNG phiếu, và suất redirect
   * duy nhất (`GUIDE_REDIRECT_CTA_BUDGET = 1`, ngân sách đó GỠ ở AI-MENU-A — nút thẻ nay có chỗ riêng `AI_MENU_LEAD_RESERVE`, không còn đẩy trang chat nào khỏi menu) đã dùng cho Q2 (`DUTY-NO-BANK-ACCOUNT`). App chưa có màn liệt kê phiếu chờ (`exp-vote.ts`).
   */
  {
    id: "DUTY-OPEN-VOTE",
    triggers: ["OPEN", "ASKED_NEXT", "TASK_DONE", "RETURN_AFTER_ABSENCE"],
    roles: ["owner", "admin", "treasurer", "member"],
    when: [{ signal: "myOpenVoteCount", op: "gt", value: 0 }],
    band: "DUTY",
    priority: 75,
    templateVi: "Có {soPhieu} khoản chi đang chờ bạn bỏ phiếu: {tenKhoanChi}.",
    vars: { soPhieu: "myOpenVoteCount" },
    ctaless: "informational",
    claims: [{ clause: "Có {soPhieu} khoản chi đang chờ bạn bỏ phiếu: {tenKhoanChi}", proof: "myOpenVoteCount" }],
    variants: [
      {
        id: "DUTY-OPEN-VOTE/mot",
        when: [{ signal: "myOpenVoteCount", op: "eq", value: 1 }],
        templateVi: "Khoản chi {tenKhoanChi} đang chờ bạn bỏ phiếu.",
        claims: [{ clause: "Khoản chi {tenKhoanChi} đang chờ bạn bỏ phiếu", proof: "myOpenVoteCount" }],
      },
    ],
  },
  /**
   * 🔴🔴 US-AIV2-0111 C07g (@UC0216 @UC0178) — NGƯỜI ĐANG CHỜ DUYỆT VÀO NHÓM. E2E 28/09 (`dung`, chờ duyệt vào Lớp 2A Kim Đồng) nghe
   * *"Bạn chưa có nhóm nào. Tạo nhóm để bắt đầu…"* — nửa đúng (chưa có nhóm HOẠT ĐỘNG) và đẩy họ đi tạo một nhóm trùng thay vì chờ.
   *
   * Bậc `DUTY` (không `SETUP`): nó THẮNG `SETUP-NO-GROUP` của người 0 nhóm — người đang chờ vào một nhóm không cần lời mời tạo nhóm; với
   * người ĐÃ có nhóm khác, nó đứng CÙNG bậc với nghĩa vụ tiền (sắp sau theo `priority`), không đè lên chúng.
   * Nêu VAI duyệt (chủ nhóm · quản trị — đúng `@RequireGroupRoles(OWNER, ADMIN)` của `POST /members/approve`), KHÔNG nêu tên người
   * (P5.3 — lưới `coverage.invariants`). Nút = `JOIN-REQUEST-MINE` (xem / rút lời xin), trang redirect không executor.
   * `roles: ["*"]` vì người chờ duyệt thường CHƯA có vai ở đâu (`role === null`); người đã có nhóm khác cũng có thể đang chờ.
   */
  {
    id: "JOIN-PENDING-MINE",
    triggers: ["OPEN", "ASKED_NEXT", "TASK_DONE", "RETURN_AFTER_ABSENCE"],
    roles: ["*"],
    // MỘT thẻ cho 1 hay nhiều lời xin (PK-4: cùng bậc không hai thẻ trỏ cùng trang) — `{tenNhomCho}` là tên nhóm, hoặc vài tên nối bằng dấu phẩy.
    when: [{ signal: "myJoinPendingCount", op: "gt", value: 0 }],
    band: "DUTY",
    priority: 45,
    templateVi: "Lời xin vào nhóm {tenNhomCho} của bạn đang chờ chủ nhóm hoặc quản trị viên duyệt. Khi được duyệt, bạn sẽ thấy nhóm ngay.",
    claims: [{ clause: "Lời xin vào nhóm {tenNhomCho} của bạn đang chờ chủ nhóm hoặc quản trị viên duyệt", proof: "myJoinPendingCount" }],
    // 🔴 KHÔNG nút riêng — `JOIN-REQUEST-MINE` là trang `redirect`, và ngân sách thẻ-trỏ-redirect (`GUIDE_REDIRECT_CTA_BUDGET = 1`, QĐ PO
    // 25/09 — ngân sách đó GỠ ở AI-MENU-A — nút thẻ nay có chỗ riêng `AI_MENU_LEAD_RESERVE`, không còn đẩy trang chat nào khỏi menu; thêm nút cho thẻ này là việc riêng, chưa làm) đã tiêu cho `CONFIG-CHANGED-REVIEW`. Lối "xem/rút lời xin" vẫn CÓ: trang đó có
    // dòng catalog menu (`menu-catalog.ts`) và chỉ ra nút cho người có lời xin chờ (`AiDoerService.menuPagesFor`, lease C07g). Bước tiếp theo là của người duyệt.
    ctaless: "waiting_on_others",
  },
  /**
   * 🔴🔴 US-AIV2-0113 M05 (@UC0214 @UC0178) — NGƯỜI CÓ LỜI MỜI. E2E 28/09 (`khach`, lời mời email vào FC Văn Phòng) nghe *"Bạn chưa có nhóm
   * nào. Tạo nhóm…"* — người được mời là người dùng mới đông nhất, và câu đó đẩy họ đi tạo một nhóm trùng.
   *
   * Bậc `DUTY` như `JOIN-PENDING-MINE`: THẮNG `SETUP-NO-GROUP` của người 0 nhóm. CHỈ người chưa có nhóm (review #1103 M1 — xem `when`).
   * Đứng TRƯỚC lời xin chờ duyệt: lời mời là việc CHÍNH người này làm được ngay, lời xin là chờ người khác.
   * Hai thẻ không trùng việc: lời mời ĐÍCH DANH chưa nhận (`myInviteCount`) ≠ link đã bấm đang chờ duyệt (ghế xếp hàng, C07g).
   * `roles: ["*"]`: người chưa có nhóm thì `role === null`.
   */
  {
    id: "INVITE-PENDING-MINE",
    triggers: ["OPEN", "ASKED_NEXT", "TASK_DONE", "RETURN_AFTER_ABSENCE"],
    roles: ["*"],
    // 🔴 review #1103 M1 (QĐ điều phối, phạm vi §M05): CHỈ người CHƯA có nhóm nào. Với người đã có nhóm, bậc DUTY của thẻ này từng che
    // SETUP/IDLE của nhóm họ đang chạy; họ vẫn HỎI được ("tôi có lời mời…" — lượt INVITE-ACCEPT trả lời mọi người).
    when: [
      { signal: "hasAnyGroup", op: "falsy" },
      { signal: "myInviteCount", op: "gt", value: 0 },
    ],
    band: "DUTY",
    priority: 48,
    // 🔴 GUIDE-SCREEN-TRUTH (lô review 15/10 G-1, @UC0214): màn «Lời mời của tôi» CHỈ ĐỌC (không nút nhận/từ chối — token không rời DB,
    // `my_invitations_screen.dart`) ⇒ cùng ý câu trang `INVITE-ACCEPT`: nhận bằng link trong email/tin nhắn mời.
    templateVi: "{cauLoiMoi} Để vào nhóm, bạn mở link trong email hoặc tin nhắn mời nhé; mục «Lời mời của tôi» chỉ để xem lại thôi.",
    claims: [{ clause: "{cauLoiMoi}", proof: "myInviteCount" }],
    // 🔴 KHÔNG nút riêng — `INVITE-ACCEPT` là trang `redirect`, ngân sách thẻ-trỏ-redirect (`GUIDE_REDIRECT_CTA_BUDGET = 1`, QĐ PO 25/09 — ngân sách đó GỠ ở AI-MENU-A — nút thẻ nay có chỗ riêng `AI_MENU_LEAD_RESERVE`, không còn đẩy trang chat nào khỏi menu;
    // thêm nút cho thẻ này là việc riêng, chưa làm). Nút "Lời mời của tôi" vẫn CÓ ở lời chào tab toàn cục: `AiDoerService.context` GHIM chính trang đó
    // ngay sau nút thẻ khi người này có lời mời (`inviteLeadOf` → `pinned` của hàm ghép menu khám phá). Câu tự chỉ đường cho mọi phạm vi.
    ctaless: "informational",
  },
  /**
   * 🔴 US-AIV2-0111 C07g (@UC0216) — CHỦ NHÓM / QUẢN TRỊ có người đang xin vào (gồm lời xin NHẬN SUẤT "Xin nhận chỗ: X" sau C11 — cùng
   * hàng chờ). Một lời xin không ai duyệt = một người đứng ngoài quỹ vô thời hạn (docblock `pendingTotalCount`). Số = ĐÚNG tập của màn
   * Thành viên (`countJoinQueue`). Không `{tenNhom}`: thiếu tên nhóm không được làm câm một việc chờ mình (cùng lẽ `OWE-NO-NAME`).
   */
  {
    id: "DUTY-JOIN-REQUESTS",
    triggers: ["OPEN", "ASKED_NEXT", "TASK_DONE", "RETURN_AFTER_ABSENCE"],
    roles: ["owner", "admin"],
    when: [{ signal: "pendingJoinRequests", op: "gt", value: 0 }],
    band: "DUTY",
    priority: 70,
    templateVi: "Có {soLoiXin} lời xin vào nhóm đang chờ bạn duyệt. Bạn vào danh sách Thành viên của nhóm để duyệt nhé.",
    vars: { soLoiXin: "pendingJoinRequests" },
    claims: [{ clause: "Có {soLoiXin} lời xin vào nhóm đang chờ bạn duyệt", proof: "pendingJoinRequests" }],
    // 🔴 KHÔNG nút (tạm): `GRP-JOIN-REQUEST` là trang `redirect` ⇒ cùng ngân sách `GUIDE_REDIRECT_CTA_BUDGET` như thẻ trên (đo 25/09: thêm nút là
    // đẩy `GROUP-BANK-ACCOUNT` khỏi menu chủ nhóm/quản trị; ngân sách đó GỠ ở AI-MENU-A — nút thẻ nay có chỗ riêng `AI_MENU_LEAD_RESERVE`, không còn đẩy trang chat nào khỏi menu; thêm nút vẫn là việc riêng, chưa làm). Câu tự chỉ
    // đường (màn Thành viên có mục ghim người chờ duyệt); gõ "duyệt người xin vào" vẫn mở đúng trang.
    ctaless: "informational",
  },
  /**
   * 🔴🔴 US-AIV2-0112 H05 (@UC0322) — TIẾN ĐỘ ĐỢT cho người QUẢN LÝ. Chỉ bắn khi nút CÒN NGƯỜI để nhắc (review #1064 H1 — tín hiệu đọc
   * `remindableSeats` = tập của chính nút; đợt cuối mà người còn lại đều đã báo chuyển ⇒ im, không mời vào `NO_UNPAID_MEMBER`). Đo 28/09: chủ nhóm có 6 bé chưa đóng nhận *"Chào bạn! Mình giúp
   * được gì?"*. Số người = mẫu số của cửa GUI (`tallyMemberProgress`, `waived` ra khỏi mẫu số — #1044), số tiền theo redaction của cửa GUI
   * (ẩn ⇒ `•••`). Vai = vai của trang nút (`DEBT-REMIND`); thành viên KHÔNG nghe tiến độ cả đợt (member chỉ thấy số của mình).
   * Bậc `DUTY`, `priority` thấp nhất bậc: nghĩa vụ CỦA CHÍNH người quản lý (khoản mình còn nợ, lời báo chờ mình duyệt) đứng trước.
   * Nút "Nhắc" = kênh NHẮC riêng tư từng người (P5.3) — thẻ KHÔNG nêu tên ai. Có `TASK_DONE` như mọi thẻ DUTY (lưới PK-5: người quản lý
   * vừa ghi xong một việc mà đợt còn người chưa đóng không được im); thẻ nghĩa vụ CỦA CHÍNH họ đứng trước theo `priority`.
   */
  {
    id: "DUTY-COLLECTION-PROGRESS",
    triggers: ["OPEN", "ASKED_NEXT", "TASK_DONE", "RETURN_AFTER_ABSENCE"],
    roles: ["owner", "admin", "treasurer"],
    when: [{ signal: "openCollectionRemindable", op: "truthy" }],
    band: "DUTY",
    priority: 40,
    // US-AIV2-tourguide-0004: nút gửi MỘT lượt nhắc cho mọi người chưa đóng nhận được lời nhắc (mỗi người nhận riêng) — câu nói đúng việc
    // đó, không hứa "riêng từng người" (việc nút không làm).
    templateVi: "{tienDoDot}. Bạn có thể nhắc những người chưa đóng.",
    cta: { page: "DEBT-REMIND", label: "Nhắc người chưa đóng" },
    // US-AIV2-tourguide-0003 (K12): câu nói ĐÚNG MỘT đợt ⇒ nút mang đợt đó; `DEBT-REMIND.ready` kiểm đợt đó còn người NHẬN được lời nhắc (K11).
    ctaSubject: "openCollectionProgress",
    claims: [
      { clause: "{tienDoDot}", proof: "openCollectionRemindable" },
      { clause: "Bạn có thể nhắc những người chưa đóng", proof: "door" },
    ],
    variants: [
      {
        id: "DUTY-COLLECTION-PROGRESS/khong-ai-nhan",
        outcomes: ["NO_REACHABLE_MEMBER"],
        templateVi: "{tienDoDot}. Những suất chưa đóng của đợt này đều chưa có ai dùng app, nên mình chưa nhắc trong app được.",
        claims: [
          { clause: "{tienDoDot}", proof: "openCollectionRemindable" },
          { clause: "chưa nhắc trong app được", proof: "door" },
        ],
        note: "K11 — cùng câu lý do NO_REACHABLE_MEMBER của catalog lúc bấm.",
      },
      {
        // 🔴 G-4 GUIDE-FROZEN-FUND (@UC0361 @UC9300): đợt nằm ở quỹ ĐÓNG BĂNG — cửa nhắc 409 (`CollectionAccessGuard`) và ô đợt của trợ lý rỗng ⇒ không
        // mời nhắc; câu tiến độ vẫn là sự thật. Loader chỉ chọn đợt này khi KHÔNG còn đợt nhắc được nào ở quỹ mở (hỗn hợp ⇒ nói đợt quỹ mở).
        id: "DUTY-COLLECTION-PROGRESS/quy-khoa",
        outcomes: ["FUND_FROZEN"],
        templateVi: "{tienDoDot}. Quỹ của đợt này đang tạm khoá nên chưa nhắc được.",
        claims: [
          { clause: "{tienDoDot}", proof: "openCollectionRemindable" },
          { clause: "Quỹ của đợt này đang tạm khoá nên chưa nhắc được", proof: "door" },
        ],
        note: "G-4 — đợt ở quỹ đóng băng: không mời nhắc (không đường mở băng trong app).",
      },
      {
        id: "DUTY-COLLECTION-PROGRESS/vua-nhac",
        outcomes: ["RECENTLY_REMINDED"],
        when: [{ signal: "openCollectionRemindedRecently", op: "truthy" }],
        templateVi: "{tienDoDot}. Đợt này đã được nhắc lúc {lucNhac}. Từ {ngayNhacLai}, mình sẽ gợi ý nhắc lại nhé.",
        claims: [
          { clause: "{tienDoDot}", proof: "openCollectionRemindable" },
          { clause: "Đợt này đã được nhắc lúc {lucNhac}", proof: "openCollectionRemindedRecently" },
          { clause: "Từ {ngayNhacLai}, mình sẽ gợi ý nhắc lại", proof: "openCollectionRemindedRecently" },
        ],
        note: "QĐ PO Q7 (US-AIV2-tourguide-0006) — lần nhắc TAY gần nhất < 3 ngày ⇒ không mời nhắc. «Đợt này đã được nhắc» (không «Bạn đã nhắc»): dấu vết nhắc không ghi AI bấm — bảng PO-DUYET-CAU 0006.",
      },
      {
        id: "DUTY-COLLECTION-PROGRESS/khong-nut",
        outcomes: ["unknown", "NOTHING_TO_REMIND", "SUBJECT_GONE"],
        templateVi: "{tienDoDot}.",
        claims: [{ clause: "{tienDoDot}", proof: "openCollectionRemindable" }],
      },
    ],
  },
  /**
   * 🔴 US-AIV2-0112 H05 (@UC0322) — ĐANG THU mà nhóm CHƯA CÓ số tài khoản nhận tiền nào (không số tài khoản, không ảnh QR). Mọi người không
   * biết chuyển vào đâu ⇒ đó là việc của người quản lý, nên bậc `DUTY`. Vai = vai đổi được STK (`GRP-BANK-QR`: chủ nhóm · quản trị — thủ
   * quỹ bấm vào là 403).
   * 🔴 CÓ NÚT `GRP-BANK-QR` — QĐ PO 01/10/2026 Q2 (US-AIV2-tourguide-0004). Trang `redirect` ⇒ ăn suất DUY NHẤT của ngân sách thẻ-trỏ-redirect
   * (`GUIDE_REDIRECT_CTA_BUDGET = 1`, QĐ 25/09) — suất mà Q4 vừa trả (`CONFIG-CHANGED-REVIEW` thôi trỏ `GRP-ACTIVITY`). AI-MENU-A: ngân sách đó GỠ ở AI-MENU-A — nút thẻ nay có chỗ riêng `AI_MENU_LEAD_RESERVE`, không còn đẩy trang chat nào khỏi menu;
   * cửa «thẻ-redirect+ghim» của `menu-chip-cap.invariants.spec.ts` mồi bằng chính `GRP-BANK-QR`.
   */
  {
    id: "DUTY-NO-BANK-ACCOUNT",
    triggers: ["OPEN", "ASKED_NEXT", "TASK_DONE", "RETURN_AFTER_ABSENCE"],
    roles: ["owner", "admin"],
    when: [
      { signal: "openCollectionCount", op: "gt", value: 0 },
      { signal: "bankAccountMissing", op: "truthy" },
    ],
    band: "DUTY",
    /*
     * 🔴 UX-GUIDE-POLISH MED-1 (@UC0353 · nghiệm thu P02 15/10) — 42, TRÊN `DUTY-COLLECTION-PROGRESS` (40): thiếu tài khoản nhận tiền CHẶN dòng tiền vào,
     * còn nhắc người chưa đóng khi chưa có chỗ nhận là nhắc vào khoảng không. Bản 35 (thấp nhất bậc) ⇒ chủ nhóm bận (khoản riêng + lời báo chờ + nhắc)
     * mất nút «Cài tài khoản nhận tiền» vì trần `GUIDE_MAX_CARD_BUTTONS`. Luật bậc giữ nguyên (`priority` chỉ sắp TRONG bậc) — không cơ chế ưu tiên thứ hai.
     */
    priority: 42,
    templateVi: "Nhóm {tenNhom} chưa có số tài khoản nhận tiền. Bạn cài tài khoản để mọi người biết chuyển tiền vào đâu nhé.",
    cta: { page: "GRP-BANK-QR", label: "Cài tài khoản nhận tiền", shows: "bank_account_setup" },
    claims: [
      { clause: "chưa có số tài khoản nhận tiền", proof: "bankAccountMissing" },
      { clause: "Bạn cài tài khoản", proof: "door" },
    ],
    variants: [
      {
        id: "DUTY-NO-BANK-ACCOUNT/khong-nut",
        outcomes: ["unknown"],
        templateVi: "Nhóm {tenNhom} chưa có số tài khoản nhận tiền.",
        claims: [{ clause: "chưa có số tài khoản nhận tiền", proof: "bankAccountMissing" }],
      },
    ],
  },
  /**
   * 🔴 US-AIV2-0112 H05 (@UC0322) — ĐÃ ĐÓNG ĐỦ. Đo 28/09: `linh` đã đóng đủ nhận lời chào chung chung. Bậc `IDLE` (thấp nhất): chỉ nói khi
   * không còn việc gì ở bậc trên. §3.7: `myOpenDuesSettled` chỉ đúng khi MỌI khoản ở đợt đang mở đã ĐƯỢC XÁC NHẬN — lời báo chờ đối chiếu
   * không phải "đã đóng". Hai vế `eq 0` dưới là phòng thủ hai lớp (loader đã loại trừ). Không `RETURN_AFTER_ABSENCE`: `RECAP-ON-RETURN`
   * đã nói "bạn không còn khoản nào cần đóng" — hai câu cùng ý trong một bậc là lặp.
   */
  {
    id: "IDLE-ALL-PAID",
    triggers: ["OPEN", "ASKED_NEXT"],
    roles: ["owner", "admin", "treasurer", "member"],
    when: [
      { signal: "myOpenDuesSettled", op: "truthy" },
      { signal: "myUnpaidDueCount", op: "eq", value: 0 },
      { signal: "myClaimedDueCount", op: "eq", value: 0 },
    ],
    band: "IDLE",
    priority: 50,
    templateVi: "Bạn đã đóng đủ các khoản đang thu ở nhóm {tenNhom} rồi, cảm ơn bạn nhé!",
    ctaless: "informational",
    claims: [{ clause: "Bạn đã đóng đủ các khoản đang thu", proof: "myOpenDuesSettled" }],
  },
  // ——— RECAP: lượt QUAY LẠI sau một khoảng vắng. Bậc IDLE — thấp nhất, nên nó chỉ nói khi KHÔNG còn việc nào ở bậc trên.
  {
    /**
     * 🔴 US-AIV2-0063-S03 (@UC0185) — "lâu rồi bạn chưa ghé" là câu NÓI SAU CÙNG, không phải câu nói đầu.
     *
     * Bậc `IDLE` là chủ ý: người quay lại mà đang CÒN NỢ thì `DUTY-MY-UNPAID` (bậc `DUTY`) thắng, và họ nghe đúng thứ cần
     * nghe. Thẻ này chỉ lên tiếng khi mọi bậc trên đều rỗng — tức "trong lúc bạn vắng, nhóm vẫn chạy và bạn không nợ gì".
     *
     * `openCollectionCount > 0` trong `when[]` không phải để lọc cho chặt: nếu nhóm không có đợt thu nào đang mở thì câu
     * "đang có 0 đợt thu" là một câu VÔ NGHĨA, và thẻ `SETUP-NO-COLLECTION` mới là thứ đúng để nói lúc đó.
     *
     * `ctaless: "informational"` — đây là một lời KỂ, không phải một việc phải làm; đính một nút vào nó là biến bản tóm
     * tắt thành lời giục.
     */
    id: "RECAP-ON-RETURN",
    triggers: ["RETURN_AFTER_ABSENCE"],
    /*
     * 🔴 US-AIV2-tourguide-0002 (@UC0360): bốn vai CÓ GHẾ, không `"*"`. Thẻ đọc `openCollectionCount`/`myUnpaidDueCount` — tín hiệu NHÓM,
     * không bao giờ được nạp cho người không ghế (`SIGNAL_LOAD_ROLES`) ⇒ với vai `null` thẻ chưa từng nói được (loader khai chúng
     * "không biết"). `"*"` là lời khai sai về người nhận, chốt `signal-load.test.ts` bắt; hành vi không đổi (ô `RETURN_AFTER_ABSENCE` ×
     * chưa-thuộc-nhóm vẫn có `SETUP-NO-GROUP`).
     */
    roles: ["owner", "admin", "treasurer", "member"],
    when: [
      { signal: "hasAnyGroup", op: "truthy" },
      { signal: "openCollectionCount", op: "gt", value: 0 },
      /**
       * 🔴🔴 HIGH (review #856 vòng 2) — VẾ NÀY LÀ THỨ DUY NHẤT CHO PHÉP CÂU DƯỚI NÓI VỀ TIỀN.
       *
       * Bản đầu không có nó, và câu vẫn khẳng định *"bạn không còn khoản nào cần đóng"*. Chuỗi nhân quả:
       * `journey()` lọc `ctaReachable` **TRƯỚC** khi chọn bậc · `DUTY-MY-UNPAID` trỏ `PAY-REPORT` · trang
       * đó còn `draft` ⇒ `isRunnable=false` ở **mọi env** ⇒ thẻ `DUTY` **luôn** bị loại ⇒ bậc `IDLE`
       * thắng ⇒ người đang nợ 3 khoản nghe *"không còn khoản nào cần đóng"*, cho **cả 4 vai**.
       *
       * Im lặng thì người dùng đi kiểm chỗ khác; **một câu sai về khoản phải đóng thì họ tin và không
       * đóng.** Đó là loại sai đắt nhất trong phần mềm quỹ nhóm.
       *
       * 🔴 Vì sao `when[]` chứ không phải "bỏ mệnh đề tiền khỏi câu": mệnh đề đó CÓ ÍCH (*"bạn không nợ
       * gì"* là điều người quay lại muốn nghe nhất). Cái sai không phải nói về tiền — mà là nói về tiền
       * **khi không có dữ liệu đứng sau**. Đưa tín hiệu vào `when[]` còn cho không một lớp nữa:
       * `signalsKnown()` loại thẻ khi `myUnpaidDueCount` **không đọc được** — tức thẻ tự im khi loader
       * hỏng, thay vì đoán 0 rồi nói câu đối lập.
       */
      { signal: "myUnpaidDueCount", op: "eq", value: 0 },
    ],
    band: "IDLE",
    priority: 10,
    templateVi: "Lâu rồi mới thấy bạn ghé! Nhóm {tenNhom} đang có {soDot} đợt thu mở, và bạn không còn khoản nào cần đóng.",
    vars: { soDot: "openCollectionCount" },
    ctaless: "informational",
    // US-AIV2-tourguide-0004 (K16): "không còn khoản nào" đếm CÙNG tập "khoản của tôi" — mọi đợt, kể cả đợt đã đóng sổ (loader).
    claims: [
      { clause: "Lâu rồi mới thấy bạn ghé", proof: "trigger" },
      { clause: "đang có {soDot} đợt thu mở", proof: "openCollectionCount" },
      { clause: "bạn không còn khoản nào cần đóng", proof: "myUnpaidDueCount" },
    ],
  },
  // ═══ US-AIV2-0065 (@UC0217 @UC0218) — THƯ VIỆN TÌNH HUỐNG: năm thẻ PO duyệt câu chữ 24/09/2026 ═══
  //
  // 🔴 Cả năm thẻ đều có CHỖ PHÁT nối trong CÙNG commit (`gen-guide-triggers.mjs` đếm) — viết thẻ cho một trigger
  // chưa ai phát là biến một ô ⚪ trung thực thành một 🔴 lời hứa chết, đúng thứ `0065b` dựng bảng đó để dọn.
  {
    /**
     * 🔴 `TASK_FAILED` — VIỆC HỎNG THÌ NÓI HỎNG, và nói cho người dùng biết ĐƯỜNG ĐI TIẾP.
     *
     * Bậc `BLOCKED`: đây là ngõ cụt tức thì, nó phải thắng mọi lời nhắc khác. Người vừa bấm Xác nhận mà hệ thống báo lỗi
     * không cần nghe "bạn còn 2 khoản cần đóng" — họ cần biết khoản vừa rồi **chưa vào sổ**.
     *
     * 🔴 CÂU PHẢI NÓI RÕ "CHƯA GHI ĐƯỢC" — đây là vế tiền, không phải vế giọng điệu. Lượt hỏng nghĩa là executor đã ném
     * TRONG tx ⇒ **rollback toàn bộ** (`confirmTask`). Một câu mơ hồ kiểu "có trục trặc" để người dùng tưởng khoản đã
     * ghi rồi và không làm lại ⇒ sổ thiếu một bút toán mà không ai biết. §3.7: máy không bao giờ được để người dùng
     * hiểu nhầm về trạng thái một đồng tiền.
     *
     * 🔴 KHÔNG mang `{lyDo}`/mã lỗi — PO duyệt câu có `{lyDo}`, nhưng đưa nó vào THẺ đòi một khoá mới trong
     * `GUIDE_CONTEXT_VALUES`, và bảng đó là **cửa DUY NHẤT** cho dữ liệu chảy vào lời chào (allowlist cấu trúc của P5.3,
     * khoá `toEqual(["tenNhom"])`). Mở cửa đó cho một chuỗi TỰ DO từ tầng lỗi là mở đúng đường mà lưới kia sinh ra để
     * đóng. Mã lỗi đi trong trường máy-đọc `failureCode` của lượt (US-AIV2-0109, không còn in trong câu), và thẻ này ĐÍNH
     * THÊM vào lượt ấy chứ không thay nó. Giữ ý của PO ("vì sao hỏng" + "thử lại"), không mở cửa mới.
     *
     * `ctaless: "blocked"` — thử lại = gõ lại/bấm lại chính việc vừa rồi, và lượt `TASK_FAILED` đã mang sẵn menu việc
     * (`menuTurn`). Một nút `run_page` tĩnh ở đây sẽ trỏ **sai trang** với mọi trang trừ một.
     */
    id: "TASK-FAILED-RETRY",
    triggers: ["TASK_FAILED"],
    roles: ["*"],
    when: [{ signal: "turnFailed", op: "truthy" }],
    band: "BLOCKED",
    priority: 100,
    // 🔴 QĐ PO 27/09/2026 (PO-DUYET-MA-TRAN câu 4): "Việc này mình chưa làm được…" thay "Khoản này mình chưa ghi được…". Thẻ
    // hiện cho MỌI việc hỏng, kể cả việc không phải một khoản tiền (máy ảo 27/09: nhắc nợ hỏng ⇒ trợ lý nói "Khoản này…").
    templateVi: "Việc này mình chưa làm được nên sổ chưa đổi gì cả. Bạn thử lại giúp mình nhé.",
    ctaless: "blocked",
    claims: [{ clause: "Việc này mình chưa làm được nên sổ chưa đổi gì cả", proof: "turnFailed" }],
  },
  {
    /**
     * 🔴 `STUCK` — BA LƯỢT CHƯA RA VIỆC GÌ THÌ ĐỪNG HỎI LẠI LẦN THỨ TƯ (PO chốt số **3**, 24/09/2026).
     *
     * Vòng lặp "mình chưa rõ ý bạn" là cách trợ lý mất người dùng: mỗi lượt trượt nó lại hỏi cùng một câu, và người dùng
     * không có cách nào biết mình nên gõ khác đi thế nào. Sau ba lần, đổi từ HỎI sang MỜI CHỌN.
     *
     * Bậc `BLOCKED` cùng lý do `TASK-FAILED-RETRY`: người đang bí cần một lối ra ngay, không cần nghe việc khác.
     * `priority` thấp hơn để nếu (hiếm) cả hai cùng bắn thì câu về TIỀN đứng trước.
     *
     * 🔴 `ctaless: "blocked"` chứ không phải một nút: PO duyệt "+ menu", và MENU chính là thứ lượt này đã mang
     * (`renderGuide` đổ tiếp trang được phép cho đủ trần sau nút của thẻ). Đính thêm một `run_page` tĩnh là CHỌN HỘ một
     * việc cho người mà ta vừa thừa nhận là mình chưa hiểu — sai đúng ở chỗ vừa nhận sai.
     */
    id: "STUCK-OFFER-MENU",
    triggers: ["STUCK"],
    roles: ["*"],
    when: [{ signal: "turnsWithoutProgress", op: "gte", value: 3 }],
    band: "BLOCKED",
    priority: 90,
    // US-AIV2-tourguide-0004: câu không nói "chưa hiểu" — lượt vừa rồi có thể là câu quá dài (chưa đọc) hoặc một việc đã hiểu mà không làm được.
    // 🔴 US-AIV2-tourguide-0006 (@UC0367 · TG-INV-8 · K18): thẻ này ĐÍNH SAU câu lý do của lượt (`fallbackTurn`), không thay nó ⇒ hai biến thể
    // "chưa hiểu" / "câu dài quá" của 0004 bị GỠ: câu lý do đứng ngay trước đã nói đúng điều đó — giữ lại là nói hai lần.
    templateVi: "Mấy lượt vừa rồi mình chưa giúp được bạn, xin lỗi nhé. Bạn chọn thử một việc bên dưới nha.",
    ctaless: "blocked",
    claims: [{ clause: "Mấy lượt vừa rồi mình chưa giúp được bạn", proof: "turnsWithoutProgress" }],
  },
  {
    /**
     * 🔴 `MY_ROLE_CHANGED` — VAI ĐỔI THÌ QUYỀN ĐỔI, và người ta phải biết mình vừa làm được thêm gì.
     *
     * Đây là tin duy nhất trong năm thẻ mang tin TỐT và mang một **năng lực mới**. Không nói thì người vừa được giao làm
     * thủ quỹ vẫn tưởng mình không ghi thu/chi được, và cái ghế đó nằm không.
     *
     * 🔴 CÂU KHÔNG NÊU VAI CỤ THỂ, dù PO duyệt câu có "thủ quỹ". Tín hiệu `myRoleChanged` là một boolean *"vai vừa đổi"*
     * — nó KHÔNG nói đổi thành vai nào, và nó cũng đúng cho chiều NGƯỢC (bị hạ từ thủ quỹ xuống thành viên). In "bạn vừa
     * được giao làm thủ quỹ" cho người vừa BỊ THU quyền là nói sai về quyền trong một app tài chính. Đưa tên vai vào câu
     * đòi một chỗ trống chuỗi mới ⇒ lại là `GUIDE_CONTEXT_VALUES` (xem `TASK-FAILED-RETRY`). Giữ ý của PO — *"vai đổi,
     * giờ bạn làm được việc khác"* — bằng một câu đúng cho CẢ HAI chiều, và để nút dẫn tới chỗ nói rõ.
     *
     * Nút `READ-PEOPLE-MINE` (`ALL_ROLES`, tier `read`) là trang DUY NHẤT trong Sổ trả lời "ai giữ vai gì" — cùng nút với
     * `BLOCKED-ASK-MANAGER`, và hai thẻ đó ở HAI bậc khác nhau nên không đụng luật "một bậc, một trang".
     *
     * Bậc `HYGIENE`: là tin đáng biết, nhưng nó KHÔNG được chen lên trước một khoản phải đóng.
     */
    id: "ROLE-CHANGED-REVIEW",
    triggers: ["MY_ROLE_CHANGED"],
    roles: ["owner", "admin", "treasurer", "member"],
    when: [{ signal: "myRoleChanged", op: "truthy" }],
    band: "HYGIENE",
    priority: 80,
    templateVi: "Vai của bạn ở nhóm {tenNhom} vừa đổi, nên việc bạn làm được cũng khác đi. Bạn xem lại vai của mình cho chắc nhé.",
    // US-AIV2-tourguide-0003 (K14): "vai của tôi ở các nhóm" = chỉ số `nhóm của tôi` (nhóm + vai của chính người hỏi).
    cta: { page: "READ-PEOPLE-MINE", label: "Xem vai của tôi", fill: { chiSo: "nhóm của tôi" } },
    claims: [
      { clause: "Vai của bạn ở nhóm {tenNhom} vừa đổi", proof: "myRoleChanged" },
      { clause: "Bạn xem lại vai của mình cho chắc nhé", proof: "door" },
    ],
    variants: [
      {
        id: "ROLE-CHANGED-REVIEW/khong-nut",
        outcomes: ["unknown"],
        templateVi: "Vai của bạn ở nhóm {tenNhom} vừa đổi, nên việc bạn làm được cũng khác đi.",
        claims: [{ clause: "Vai của bạn ở nhóm {tenNhom} vừa đổi", proof: "myRoleChanged" }],
      },
    ],
  },
  /**
   * 🔴🔴 US-AIV2-tourguide-0007a (@UC0365 · TG-INV-6 · QĐ PO Q5) — PHIẾU DỞ ⇒ mời «Làm tiếp» ĐÚNG phiếu đó. Thay `STALE-UNFINISHED` (0065).
   *
   * Thẻ cũ chỉ nói sau ≥ 3 ngày và hứa *"Nhắn cho mình để làm nốt"* — KHÔNG làm được: phiếu sống ở hội thoại CŨ, còn app mở trợ lý là hội thoại MỚI
   * (`topOfStack` theo hội thoại) ⇒ máy đo K19 đỏ. Nay: MỌI tuổi, MỌI hội thoại (tín hiệu `unfinishedTaskCount` + fact `myUnfinishedTask`), câu nêu
   * ĐÚNG việc bằng chữ của Sổ (`help.title`) + nhãn đối tượng đã phân giải (đợt/quỹ — 0 số tiền, 0 chữ LLM viết), nút «Làm tiếp» mang id PHIẾU
   * (`ctaFrom: "unfinishedTask"` — trang nút = trang của phiếu, KHÔNG tĩnh). Tầng api kiểm chủ phiếu + trạng thái + cổng quyền + `ready()` TRÊN
   * phiếu lúc hiện (`GuideButtonGate`) VÀ lúc bấm (`AiDoerService.handle` — đổi hội thoại Ở ĐẦU lượt, nối bằng THAM CHIẾU).
   *
   * Hai thẻ, MỘT việc — tách vì BẬC là thuộc tính tĩnh của thẻ (B1 0007): việc TIỀN (trang `T2`) ⇒ `DUTY` (ngang nghĩa vụ — người đang báo dở một
   * khoản tiền); việc dựng nhóm (`T1`) ⇒ `SETUP`. Hai thẻ loại trừ nhau theo `unfinishedTaskMoney` ⇒ một lượt không bao giờ nói hai lần.
   *
   * Trigger: `STALE` (lời chào gộp vào khối thẻ trạng thái, sắp cùng luật bậc → priority — `composeGreeting`, không bị bậc lọc mất) + `ASKED_NEXT` (lượt "tôi cần làm gì" liệt kê nó —
   * review v2 N6; `cancelTask` cũng phát `ASKED_NEXT`: bộ lọc chính sách Q6 không mời lại cùng trang vừa huỷ).
   *
   * 🔴 Câu CHỈ ra khi nút ra (mọi kết quả không nút ⇒ IM): phiếu không còn làm được / không kiểm được thì một câu "bạn đang làm dở X" không có lối
   * đi là lời nhắc chết. Câu không tự nhắc tên nút ("bấm …") — lượt "làm gì tiếp" giữa phiếu ghi thay nút bằng «Quay lại» (TG-INV-11), và một
   * câu hứa nút vắng là câu sai.
   */
  {
    id: "RESUME-UNFINISHED",
    triggers: ["STALE", "ASKED_NEXT"],
    roles: ["owner", "admin", "treasurer", "member"],
    when: [
      { signal: "unfinishedTaskCount", op: "gt", value: 0 },
      { signal: "unfinishedTaskMoney", op: "truthy" },
    ],
    band: "DUTY",
    // Trên mọi thẻ nghĩa vụ khác: việc người dùng ĐÃ BẮT ĐẦU làm (có thể đang báo một khoản tiền) đứng trước lời nhắc việc mới.
    priority: 95,
    templateVi: "Bạn đang làm dở việc {viecDo}.",
    ctaFrom: "unfinishedTask",
    claims: [{ clause: "Bạn đang làm dở việc {viecDo}", proof: "unfinishedTaskCount" }],
    variants: [
      {
        id: "RESUME-UNFINISHED/nhieu",
        outcomes: ["ready"],
        when: [{ signal: "unfinishedOtherCount", op: "gt", value: 0 }],
        templateVi: "Bạn đang làm dở việc {viecDo}, và còn {soViecKhac} việc làm dở khác nữa.",
        vars: { soViecKhac: "unfinishedOtherCount" },
        claims: [
          { clause: "Bạn đang làm dở việc {viecDo}", proof: "unfinishedTaskCount" },
          { clause: "còn {soViecKhac} việc làm dở khác", proof: "unfinishedOtherCount" },
        ],
        note: "còn phiếu dở KHÁC đã kiểm là làm tiếp được: mời phiếu GẦN NHẤT làm được (nút), nói số ĐÃ KIỂM; không đếm chắc được ⇒ câu mặc định",
      },
      {
        id: "RESUME-UNFINISHED/im",
        outcomes: GUIDE_RESUME_SILENT_OUTCOMES,
        silent: true,
        note: "phiếu không còn làm được / không kiểm được / chính sách không mời ⇒ im (câu không nút là lời nhắc không lối đi)",
      },
    ],
  },
  {
    id: "RESUME-UNFINISHED-SETUP",
    triggers: ["STALE", "ASKED_NEXT"],
    roles: ["owner", "admin", "treasurer", "member"],
    when: [
      { signal: "unfinishedTaskCount", op: "gt", value: 0 },
      { signal: "unfinishedTaskMoney", op: "falsy" },
    ],
    band: "SETUP",
    priority: 90,
    templateVi: "Bạn đang làm dở việc {viecDo}.",
    ctaFrom: "unfinishedTask",
    claims: [{ clause: "Bạn đang làm dở việc {viecDo}", proof: "unfinishedTaskCount" }],
    variants: [
      {
        id: "RESUME-UNFINISHED-SETUP/nhieu",
        outcomes: ["ready"],
        when: [{ signal: "unfinishedOtherCount", op: "gt", value: 0 }],
        templateVi: "Bạn đang làm dở việc {viecDo}, và còn {soViecKhac} việc làm dở khác nữa.",
        vars: { soViecKhac: "unfinishedOtherCount" },
        claims: [
          { clause: "Bạn đang làm dở việc {viecDo}", proof: "unfinishedTaskCount" },
          { clause: "còn {soViecKhac} việc làm dở khác", proof: "unfinishedOtherCount" },
        ],
        note: "còn phiếu dở KHÁC đã kiểm là làm tiếp được: mời phiếu GẦN NHẤT làm được (nút), nói số ĐÃ KIỂM; không đếm chắc được ⇒ câu mặc định",
      },
      {
        id: "RESUME-UNFINISHED-SETUP/im",
        outcomes: GUIDE_RESUME_SILENT_OUTCOMES,
        silent: true,
        note: "phiếu không còn làm được / không kiểm được / chính sách không mời ⇒ im (câu không nút là lời nhắc không lối đi)",
      },
    ],
  },
  {
    /**
     * 🔴🔴 `CONFIG_CHANGED` — **WHITELIST BỐN KHOÁ** (QĐ PO 24/09/2026, nguyên văn: *"không có trong list thì không báo"*).
     *
     * Bốn khoá: mức minh bạch · người sở hữu nhóm · vai thành viên · số tài khoản nhận tiền của nhóm. Danh sách sống ở
     * `GUIDE_CONFIG_WATCHED_ACTIONS` ngay dưới thư viện này, và nó là **ALLOWLIST ĐÓNG**: khoá nào không có tên trong đó
     * thì trợ lý IM. Cài bằng blacklist ("báo mọi thứ trừ...") là đảo ngược QĐ của PO — mỗi khoá config mới ai đó thêm
     * vào repo sẽ tự động thành một thông báo mà PO chưa duyệt, và nhóm 30 người sẽ nhận một tin mỗi lần ai đó chỉnh
     * một ngưỡng nội bộ.
     *
     * Vì sao BỐN khoá đó: cả bốn đổi **ai được thấy gì hoặc ai được làm gì với TIỀN**. Đổi số tài khoản nhận tiền mà
     * không ai biết là kịch bản lừa đảo kinh điển của quỹ nhóm — người ta chuyển vào một tài khoản lạ và tưởng mình
     * vừa đóng quỹ. Đó là lý do khoá này ở trong danh sách dù nó trông giống "một cài đặt".
     *
     * 🔴 CÂU KHÔNG NÊU AI ĐỔI, dù PO duyệt câu có `{aiDoi}`. **P5.3 cấm tuyệt đối tên NGƯỜI trong kênh nhắc** — đây
     * không phải một ràng buộc kỹ thuật mà là một luật sản phẩm, và `coverage.invariants` có ca khoá nó ở tầng dữ liệu.
     * Cũng không nêu TÊN KHOÁ (`{tenCaiDat}`) vì `configChangedCount` đếm được nhiều hơn một. Câu nói đúng thứ nó biết:
     * có thay đổi, bao nhiêu, ở nhóm nào — rồi nút dẫn sang nhật ký, nơi ai-đổi-gì được kể ĐẦY ĐỦ và đúng quyền xem.
     *
     * Bậc `HYGIENE`, `priority` thấp nhất trong bậc: đáng biết, không cấp bằng nghĩa vụ.
     *
     * 🔴🔴 **CÓ NÚT `GRP-ACTIVITY` — QĐ PO 25/09/2026 ("cho phép nới"). Đọc trước khi gỡ nút đi.**
     *
     * Bản đầu của thẻ này để `ctaless` vì `GRP-ACTIVITY` là trang **`tier: "redirect"`** (mở màn hình, không chạy
     * trong chat), nên nút của nó **ăn một suất** của phần khám phá chip — và trần chip lúc đó **khít 0 dư**. Đo
     * được cái mất, có tên: trang chat **`GROUP-BANK-ACCOUNT`** (*"chuyển tiền vào đâu?"*, `@UC0213`) rơi khỏi menu
     * của **owner** và **admin**. Lưới `PK-CTA-CHAT` bắt đúng điều đó và đòi **cả hai vế** (a) nâng
     * `GUIDE_REDIRECT_CTA_BUDGET` **VÀ** (b) nâng ngân sách chip — mà (b) cần PO.
     *
     * PO đã cho phép, và **cả hai vế đã làm trong cùng commit**: `GUIDE_REDIRECT_CTA_BUDGET` 0→1 ·
     * `AI_MENU_REDIRECT_CTA_RESERVE = 1` (trần menu 22→23, trần dây tự theo vì DẪN XUẤT). Đo lại sau khi nới:
     * **0 trang chat mất chip ở cả 4 vai** — `GROUP-BANK-ACCOUNT` giữ được chip của nó.
     *
     * AI-MENU-A (@UC0353 · GOPPY-45): cả hai hằng trên ĐÃ GỠ — menu ghép theo catalog, nút thẻ có chỗ riêng (`AI_MENU_LEAD_RESERVE`,
     * `render-turn.ts`) nên một nút thẻ trỏ `redirect` không còn đẩy trang chat nào ra (khoá ở `menu-chip-cap.invariants.spec.ts`). Thẻ này
     * vẫn KHÔNG nút (Q4 01/10 — màn Hoạt động không đọc `audit_log`), lý do đó không đổi.
     */
    id: "CONFIG-CHANGED-REVIEW",
    triggers: ["CONFIG_CHANGED"],
    roles: ["owner", "admin", "treasurer", "member"],
    when: [{ signal: "configChangedCount", op: "gt", value: 0 }],
    band: "HYGIENE",
    priority: 60,
    /*
     * 🔴🔴 US-AIV2-tourguide-0004 — QĐ PO 01/10/2026 Q4: KHÔNG NÚT (đọc trước khi gắn lại nút). Đo K23: màn Hoạt động KHÔNG đọc `audit_log`
     * ⇒ nút "Xem hoạt động nhóm" mở một màn không có thay đổi mà câu vừa nói. Suất `redirect` trả về cho `DUTY-NO-BANK-ACCOUNT` (Q2).
     * Khi thay đổi là TÀI KHOẢN NHẬN TIỀN, câu nói rõ — kịch bản lừa đảo kinh điển (docblock trên) cần một lời cảnh báo trước khi chuyển.
     */
    templateVi: "Nhóm {tenNhom} vừa có {soThayDoi} thay đổi về cài đặt quan trọng.",
    vars: { soThayDoi: "configChangedCount" },
    ctaless: "informational",
    claims: [{ clause: "vừa có {soThayDoi} thay đổi về cài đặt quan trọng", proof: "configChangedCount" }],
    variants: [
      {
        id: "CONFIG-CHANGED-REVIEW/stk",
        when: [
          { signal: "configBankChanged", op: "truthy" },
          { signal: "configOtherChanged", op: "falsy" },
        ],
        templateVi: "Tài khoản nhận tiền của nhóm {tenNhom} vừa đổi. Trước khi chuyển tiền, bạn kiểm tra lại số tài khoản cho chắc nhé.",
        claims: [{ clause: "Tài khoản nhận tiền của nhóm {tenNhom} vừa đổi", proof: "configBankChanged" }],
      },
      {
        id: "CONFIG-CHANGED-REVIEW/stk-va-khac",
        when: [
          { signal: "configBankChanged", op: "truthy" },
          { signal: "configOtherChanged", op: "truthy" },
        ],
        templateVi: "Nhóm {tenNhom} vừa có {soThayDoi} thay đổi về cài đặt quan trọng, trong đó tài khoản nhận tiền của nhóm vừa đổi. Trước khi chuyển tiền, bạn kiểm tra lại số tài khoản cho chắc nhé.",
        claims: [
          { clause: "vừa có {soThayDoi} thay đổi về cài đặt quan trọng", proof: "configChangedCount" },
          { clause: "tài khoản nhận tiền của nhóm vừa đổi", proof: "configBankChanged" },
        ],
      },
    ],
  },
]);

/**
 * 🔴🔴 US-AIV2-0065 (@UC0218) — BỐN KHOÁ PO DUYỆT cho `CONFIG_CHANGED`. **ALLOWLIST ĐÓNG.**
 *
 * QĐ PO 24/09/2026, nguyên văn: *"không có trong list thì không báo"*. Mỗi phần tử là một `audit_log.action` — tức thứ
 * repo THẬT SỰ ghi lại khi khoá đó đổi, không phải một cái tên ta tự nghĩ ra. Bốn khoá của PO ánh xạ sang sáu action vì
 * "số tài khoản nhận tiền" có ba đường ghi (số tài khoản · tải mã QR · xoá mã QR) và "người sở hữu" đổi qua đường
 * lifecycle; đó là ánh xạ 1-nhiều, không phải nới danh sách.
 *
 * 🔴 VÌ SAO ĐÓNG, VÀ VÌ SAO ĐIỀU ĐÓ QUAN TRỌNG HƠN NÓ TRÔNG: một blacklist ("báo mọi `group.*` trừ vài cái") sẽ tự nuốt
 * mọi action ai đó thêm vào repo sau này — repo hôm nay đã có **22** action `group.*`, và phần lớn là chuyện nội bộ
 * (quét OCR, đọc tuỳ chọn danh tính, huỷ đơn xin vào nhóm). Báo hết là biến trợ lý thành một máy phát tin nhiễu, và tin
 * nhiễu thì người ta tắt — rồi tin THẬT về số tài khoản cũng tắt theo.
 *
 * 🔴 ĐÂY LÀ MỘT DANH SÁCH TAY, và luật của repo là "guard phải TỰ KHÁM PHÁ". Ngoại lệ có lý do và có chốt: danh sách này
 * KHÔNG phải một guard — nó là **nội dung PO duyệt**, và tự khám phá nó (quét mọi action `group.*`) chính là cái
 * blacklist vừa bị loại. Chốt thay thế là `scenario-library.test.ts` (khối `CONFIG_CHANGED`): mọi phần tử phải là một action CÓ THẬT trong mã sản phẩm
 * (grep ra được), nên một cái tên gõ sai sẽ đỏ thay vì im lặng không bao giờ khớp.
 */
export const GUIDE_CONFIG_WATCHED_ACTIONS: readonly string[] = Object.freeze([
  /**
   * 🔴 **Mức minh bạch** — và tên action ở đây là `admin_group_config_edited`, ĐỪNG "sửa lại cho đẹp".
   *
   * Bản đầu của danh sách này viết `"group.config.update"` vì chuỗi đó có thật trong repo. Đo lại thì nó chỉ tồn tại ở
   * **một danh sách ĐỌC** (`GroupConfigAuditService.GROUP_CONFIG_ACTIONS`, phía admin portal) — **không producer nào
   * ghi ra nó**. Tức whitelist của ta sẽ khớp 0 hàng, và khoá minh bạch — khoá đầu tiên PO nêu — im lặng mãi mãi mà
   * mọi lưới vẫn xanh. Producer THẬT là `AdminGroupsService` với `admin_group_config_edited`
   * (`entity: "group"`, `entityId: groupId`), và nó là đường duy nhất hôm nay ghi `groups.settings` kèm audit.
   *
   * 📌 Bài học đã trả giá ngay trong task này: *"chuỗi có trong repo"* ≠ *"chuỗi được GHI RA"*. Một danh sách đọc và
   * một danh sách ghi trông y hệt nhau trong `grep`.
   */
  "admin_group_config_edited",
  /** Số tài khoản nhận tiền của nhóm (`GroupBankService`, `entity: "group"`). */
  "group.bank.update",
  /** Mã QR nhận tiền — cùng "số tài khoản nhận tiền" dưới mắt người chuyển khoản. */
  "group.qr.upload",
  "group.qr.delete",
]);

/**
 * 🔴 US-AIV2-tourguide-0004 (Q4) — tập con của `GUIDE_CONFIG_WATCHED_ACTIONS` là "TÀI KHOẢN NHẬN TIỀN của nhóm" (số tài khoản · tải/xoá mã
 * QR). Loader đọc `action` của các thay đổi đã đếm để thẻ nói rõ *"tài khoản nhận tiền của nhóm vừa đổi"*. Chốt: `scenario-library.test.ts`
 * (⊆ danh sách theo dõi).
 */
export const GUIDE_CONFIG_BANK_ACTIONS: readonly string[] = Object.freeze(["group.bank.update", "group.qr.upload", "group.qr.delete"]);

/**
 * 🔴 VAI THÀNH VIÊN — tập action RIÊNG, vì nó neo vào một ENTITY khác.
 *
 * `RoleManagementService` ghi `entity: "group_member"`, `entityId: <id GHẾ bị đổi>` — không phải `entity: "group"`.
 * Gộp hai tập vào một truy vấn `entity: "group"` sẽ làm vai **không bao giờ khớp**; gộp vào `entity: "group_member"`
 * sẽ làm cấu hình không bao giờ khớp. Hai tập, hai truy vấn, và chính sự tách bạch này cho không một thứ quý:
 * `entityId = ghế CỦA TÔI` trả lời đúng *"vai của TÔI đổi"* mà không phải soi `diff.targetUserId`.
 */
export const GUIDE_ROLE_WATCHED_ACTIONS: readonly string[] = Object.freeze([
  "group.member.role.grant",
  "group.member.role.revoke",
]);

/**
 * 🔴 KHOÁ THỨ TƯ CỦA PO — **"người sở hữu nhóm"** — HÔM NAY CHƯA BẮT ĐƯỢC. Ghi ra, không giấu.
 *
 * PO nêu bốn khoá; ba khoá đầu (minh bạch · vai thành viên · số tài khoản) đã có action đúng hình ở hai tập trên.
 * Khoá thứ tư thì không: `lifecycle.owner_force_transferred` ghi `entity: "owner_transfer_request"`,
 * `entityId: <id của ĐƠN chuyển chủ>` — và `groupId` chỉ nằm trong `diff`. Bắt nó cần **một truy vấn thứ ba** đi qua
 * `diff->>'groupId'`, tức một phép lọc trên JSON **không có index** (`audit_log` chỉ có
 * `(entity, entityId)` · `(actorUserId, createdAt)` · một partial index cho correction chain). Quét như thế ở đúng
 * lượt người dùng đợi lâu nhất là thứ `context.ts` đã cảnh báo đích danh cho `pendingConfirmations`.
 *
 * 🔴 Vì sao GHI RA thay vì im: nếu để trống, người đọc sau sẽ thấy "whitelist 4 khoá PO duyệt" trong docblock và tin
 * rằng cả bốn đều đang chạy. *"Tài liệu nói có một tính năng không có còn tệ hơn không ghi gì"* (CLAUDE.md §4). Và
 * sự vắng mặt thì không test nào canh được — nên nó phải có ĐỊA CHỈ ở đây, kèm cái MẤT đã cân.
 *
 * ⚖️ Cái MẤT: chuyển chủ nhóm là việc HIẾM và nó ĐÃ có đường báo riêng (phiên đồng thuận `lifecycle`, tất cả người
 * bỏ phiếu đều biết) — khác hẳn ba khoá kia, nơi một người đổi và 29 người không hay. Nên đây là khoá ít mất nhất
 * trong bốn khoá, không phải một khoá bị bỏ qua tuỳ tiện.
 *
 * 🔁 Bắt nó khi: có index trên `audit_log(diff->>'groupId')`, HOẶC producer chuyển chủ ghi thêm một hàng
 * `entity: "group"` (một dòng ở `owner-transfer-request.service.ts`, đường GHI `@gate` — cần QĐ riêng).
 */
export const GUIDE_CONFIG_UNWATCHED_YET: readonly { readonly poKey: string; readonly action: string; readonly why: string }[] =
  Object.freeze([
    {
      poKey: "người sở hữu nhóm",
      action: "lifecycle.owner_force_transferred",
      why: "audit ghi entity='owner_transfer_request', groupId chỉ ở trong `diff` ⇒ cần lọc JSON không index ở đúng lượt mở tab",
    },
  ]);

/**
 * 🔴 KHOẢNG TRỐNG KHAI TƯỜNG MINH — trigger có tên mà chưa có thẻ, kèm TASK CHỦ.
 *
 * Vì sao không để lưới đỏ luôn cho đủ 17: 0061 dựng cơ chế, nội dung thuộc các task sau. Vì sao không im lặng bỏ qua:
 * *"sự vắng mặt thì không test nào canh được"* — khai ra thì mỗi khoảng trống có địa chỉ, và `MAX_SILENT_TRIGGERS` là
 * **ratchet chỉ đi XUỐNG** (cùng họ `MAX_TASK_OPEN` của G14f v1). Trigger thứ 18 thêm vào enum mà không thẻ, không khai ⇒ AC1 đỏ.
 */
/**
 * 🔴 Review #0065 MEDIUM-1 — QUYẾT ĐỊNH đứng sau một khoảng im lặng, kiểm HÌNH theo loại ở `matrix.ts` (G14h đọc chung):
 *  · `po-decision` — `ref` mang ngày `dd/mm/yyyy` của QĐ PO ("QĐ PO 24/09/2026");
 *  · `owned-task`  — `ref` là mã task còn làm tiếp (`US-…-0000`), khoảng trống có người nhận;
 *  · `structural`  — sự việc không xảy ra được với vai đó; `ref` là mã task đã đo và khai điều đó.
 * Trước bản vá, chỉ có `owner`/`reason` và G14h chỉ đo độ dài lý do ⇒ lý do "................" và owner "x" đều lọt.
 */
export interface GuideSilentDecision {
  readonly kind: "po-decision" | "owned-task" | "structural";
  readonly ref: string;
}

export interface GuideSilentTrigger {
  readonly trigger: GuideTrigger;
  readonly owner: string;
  readonly decision: GuideSilentDecision;
  readonly reason: string;
}

export const GUIDE_SILENT_TRIGGERS: readonly GuideSilentTrigger[] = Object.freeze([
  { trigger: "BEFORE_RISKY_CONFIRM", owner: "US-AIV2-0066", decision: { kind: "owned-task", ref: "US-AIV2-0066" }, reason: "cảnh báo một dòng trên phiếu, 3 luật + ngưỡng 40% (@UC0219)" },
  /**
   * ═══ 🔴 QĐ PO 24/09/2026 — BỐN TRIGGER DƯỚI ĐÂY LÀ "QUYẾT ĐỊNH KHÔNG LÀM", KHÔNG PHẢI "CHƯA KỊP LÀM" ═══
   *
   * Đọc kỹ trước khi mở một task để "làm nốt". Bản trước của bốn dòng này ghi *"thẻ CHAT thuộc thư viện tình huống"* —
   * câu đó hứa rằng `0065` sẽ làm, nên người đọc sau sẽ đi tìm xem nó làm chưa. `0065` ĐÃ CHẠY, và PO đã xem từng cái
   * rồi **quyết định không làm**, có lý do. Một khoảng trống có quyết định đứng sau thì khác hẳn một khoảng trống bị bỏ
   * quên, và tài liệu phải phân biệt được hai thứ đó — nếu không, mỗi vòng rà lại tốn một lần đo.
   *
   * 🔁 Mở lại khi: PO đổi ý, hoặc kênh push đo được là không tới nơi (người dùng tắt thông báo hệ thống).
   */
  {
    trigger: "PERIOD_START",
    owner: "US-AIV2-0065",
    decision: { kind: "po-decision", ref: "QĐ PO 24/09/2026" },
    reason:
      "QĐ PO 24/09: KHÔNG làm kỳ hụi · gói mua chung · kỳ thành viên trong đợt này — ba thứ đó chưa có người dùng thật; quỹ định kỳ đã có collection.created phủ",
  },
  {
    trigger: "DEADLINE_NEAR",
    owner: "US-AIV2-0065",
    decision: { kind: "po-decision", ref: "QĐ PO 24/09/2026 · xác nhận 27/09/2026" },
    reason: "QĐ PO 24/09 (nhóm C), PO xác nhận 27/09 khi duyệt ma trận: KHÔNG làm thẻ chat — `due.reminder` của reminder.job đã nhắc trước hạn; thêm thẻ chat là hai lời nhắc cho một khoản",
  },
  {
    trigger: "MY_ITEM_RESOLVED",
    owner: "US-AIV2-0065",
    decision: { kind: "po-decision", ref: "QĐ PO 24/09/2026 · xác nhận 27/09/2026" },
    reason: "QĐ PO 24/09 (nhóm C), PO xác nhận 27/09 khi duyệt ma trận: KHÔNG làm thẻ chat — `payment.confirmed`/`payment.declined` đã báo riêng cho người đóng ngay lúc việc xảy ra",
  },
  {
    trigger: "MILESTONE",
    owner: "US-AIV2-0065",
    decision: { kind: "po-decision", ref: "QĐ PO 24/09/2026 · xác nhận 27/09/2026" },
    reason: "QĐ PO 24/09 (nhóm C), PO xác nhận 27/09 khi duyệt ma trận: KHÔNG làm thẻ chat — `ai.guide.milestone` của kênh push ĐÃ SỐNG và nó là tin vui, hợp với push hơn là với lời chào",
  },
  /**
   * 🔴 QĐ PO 23/09/2026 — LỐI A: bỏ nhãn `DUTY_CREATED` khỏi `DUTY-MY-UNPAID`/`DUTY-CONFIRM-PENDING` thay vì nối chỗ phát.
   *
   * Đo trước khi quyết: hai thẻ đó khai 5 trigger, `DUTY_CREATED` chỉ là một — 4 trigger kia (`OPEN` · `ASKED_NEXT` ·
   * `TASK_DONE` · `RETURN_AFTER_ABSENCE`) ĐỀU có người phát, nên **hai câu chữ vẫn tới người dùng hằng ngày**. Thứ chết
   * là một NHÃN trong mảng, không phải một câu. Và sự việc đã được kênh khác phủ: `notify.ts` khai `DUTY_CREATED`
   * `covered` bởi `collection.created` (fan-out tới đúng người có due).
   *
   * ⇒ Cái MẤT đã cân và chấp nhận: **tính TỨC THÌ trong chat**. Ai đó giao nghĩa vụ trong lúc tôi đang mở chat thì trợ lý
   * không tự nói ngay; tôi biết qua push, rồi thấy lại ở lời chào lần sau. Lối B (nối chỗ phát) bị loại vì hôm nay
   * **không có đường đẩy một lượt vào chat đang mở**, và vì nó đụng luật "hai kênh, một sự việc" của `notify.ts`.
   *
   * 🔁 Mở lại khi: có đường đẩy lượt vào chat đang mở, HOẶC PO thấy khoảnh khắc "vừa bị giao nghĩa vụ" đáng nói ngay.
   * Khi đó thêm lại nhãn vào 2 thẻ trên + nối chỗ phát ở `US-AIV2-0038`, và hạ `MAX_SILENT_TRIGGERS` xuống 11.
   */
  { trigger: "DUTY_CREATED", owner: "US-AIV2-0038", decision: { kind: "po-decision", ref: "QĐ PO 23/09/2026 lối A" }, reason: "QĐ PO 23/09 lối A: kênh chat nói ở lượt sau; tức thì cần đường đẩy lượt vào chat đang mở (chưa có)" },
  /**
   * 🔴 QĐ PO 27/09/2026 (duyệt ma trận, PO-DUYET-MA-TRAN câu 3): KHAI IM CÓ CHỦ.
   *
   * Trước 27/09 dòng này là dòng DUY NHẤT còn nghĩa "chưa ai đặt câu hỏi" (phiên 24/09 không hỏi tới rủi ro tài khoản).
   * Nay PO đã được hỏi và chọn im. Làm thẻ đòi định nghĩa "rủi ro" (đăng nhập lạ? đổi thiết bị?) + nguồn tín hiệu — một
   * thiết kế bảo mật riêng. CÁI MẤT đã cân: trợ lý không nói gì khi tài khoản có chuyện.
   * `notify.ts` (kênh push) vẫn khai riêng — QĐ này là về thẻ CHAT.
   */
  {
    trigger: "ACCOUNT_RISK",
    owner: "US-AIV2-0065",
    decision: { kind: "po-decision", ref: "QĐ PO 27/09/2026" },
    reason:
      "QĐ PO 27/09 (duyệt ma trận): KHÔNG làm thẻ chat — 'rủi ro tài khoản' cần định nghĩa + nguồn tín hiệu bảo mật riêng; mở lại bằng một task thiết kế bảo mật",
  },
]);

/**
 * Ratchet: chỉ đi XUỐNG. 0062/0063/0065/0066 hạ dần; nới lên là một QĐ có tên, không phải tiện tay.
 *
 * 🔴 12 → 11 ở **0063**: `RETURN_AFTER_ABSENCE` đã có thẻ (`RECAP-ON-RETURN`) **và** có caller thật (`AiDoerService.context`
 * chọn trigger theo `users.last_seen_at`, `absence.ts`). Bốn trigger thời gian (`DEADLINE_NEAR` · `STALE` · `PERIOD_START` ·
 * `MILESTONE`) **vẫn im ở kênh CHAT** và đổi chủ sang `0065` — vì 0063 làm kênh **NGOÀI** chat, và hai kênh là hai câu hỏi
 * khác nhau (`guide/notify.ts` khai ai lo cái nào ở kênh đó, kèm bằng chứng cái nào ĐÃ có người làm).
 *
 * ⚠️ 14 → 12 ở 0062 nghĩa là `TASK_DONE` và `BLOCKED` **đã có thẻ trong thư viện** — KHÔNG có nghĩa cả hai đã tới được tay
 * người dùng (review #835). `TASK_DONE` đã nối dây thật (lượt `done`/`reply`); `BLOCKED` mới có thẻ + lưới, **chưa có caller
 * production** (cổng năng lực vẫn trả 403 problem+json) và nút của nó trỏ `READ-PEOPLE-MINE` đang `draft`. Ratchet đo *"trigger
 * này đã có ai nhận chưa"*, không đo *"người dùng đã thấy chưa"* — hai câu hỏi khác nhau, và câu thứ hai là `US-AIV2-0062b`.
 */
/**
 * 🔴 11 → 12 (QĐ PO 23/09/2026, lối A) — **lần DUY NHẤT ratchet này đi LÊN**, và nó là một QĐ có tên, không phải tiện tay.
 *
 * Bình thường ratchet chỉ đi XUỐNG: mỗi task đổ nội dung vào thư viện thì một trigger rời danh sách im lặng. Lần này ngược
 * chiều vì ta **gỡ một nhãn thừa** chứ không bỏ một câu chữ — `DUTY_CREATED` chuyển từ "có thẻ mà không ai phát" (lời hứa
 * chết, gate `G14l` đếm) sang "khai im lặng có chủ và có lý do" (trung thực, đếm được, có địa chỉ mở lại).
 *
 * Con số tăng nhưng **sự thật tốt lên**: 0 câu chữ nào mất đi, 0 người dùng nào bị ảnh hưởng, và 1 lời hứa chết biến mất.
 * Đừng đọc "12 > 11" thành "lùi một bước" — đọc kèm `guide-triggers.md` (thẻ chết 1 → 0).
 */
/**
 * 🔴🔴 12 → **7** ở `US-AIV2-0065` (@UC0217 @UC0218) — ratchet trở lại đúng chiều của nó, và đây là bước xuống LỚN NHẤT
 * từ đầu epic.
 *
 * Năm trigger rời danh sách vì cả hai vế đều có thật trong CÙNG một commit: **thẻ** (`TASK-FAILED-RETRY` ·
 * `STUCK-OFFER-MENU` · `ROLE-CHANGED-REVIEW` · `STALE-UNFINISHED` · `CONFIG-CHANGED-REVIEW`) **và chỗ phát**
 * (`ai-doer.service.ts` cho hai trigger họ-LƯỢT, `context()` cho ba trigger họ-THẾ-GIỚI-ĐỔI). Thứ tự đó là bắt buộc, không
 * phải sở thích: `gen-guide-triggers.mjs` chạy với `MAX_DEAD_CARDS` headroom 0, nên viết thẻ trước khi nối chỗ phát sẽ
 * **chặn chính commit này** — đúng thiết kế của nó.
 *
 * Bảy dòng còn lại KHÔNG phải "còn 7 việc phải làm": sáu trong bảy là QUYẾT ĐỊNH (bốn QĐ PO 24/09 không làm thẻ chat +
 * `DUTY_CREATED` lối A 23/09 + `BEFORE_RISKY_CONFIRM` có chủ là `0066`). [27/09: `ACCOUNT_RISK` nay cũng là QĐ PO — 7/7.] Đúng MỘT dòng — `ACCOUNT_RISK` — từng còn nghĩa
 * "chưa ai đặt câu hỏi". Con số của ratchet đo *"bao nhiêu trigger chưa có thẻ"*, nó không phân biệt được ba thứ đó; các
 * `reason` ở trên phân biệt. Đọc số mà không đọc lý do là đọc thiếu một nửa.
 */
export const MAX_SILENT_TRIGGERS = 7;

/**
 * Ô (trigger × vai) khai im lặng CÓ LÝ DO — khác `GUIDE_SILENT_TRIGGERS` ở chỗ trigger ĐÃ có thẻ, chỉ một vai không có.
 * Ô trống phải là ô trống THẬT, không phải "chưa kịp" (`@UC0217`).
 */
export interface GuideSilentCombo {
  readonly trigger: GuideTrigger;
  readonly role: AiPageGroupRole | null;
  readonly decision: GuideSilentDecision;
  readonly reason: string;
}

export const GUIDE_SILENT_COMBOS: readonly GuideSilentCombo[] = Object.freeze([
  /**
   * 🔴 US-AIV2-0065 — BA Ô `× chưa-thuộc-nhóm`, và cả ba là ô trống THẬT, không phải "chưa kịp".
   *
   * Ba thẻ mới của thư viện tình huống khai `roles: [owner, admin, treasurer, member]` chứ không `"*"`, nên vai `null`
   * (chưa thuộc nhóm nào) không có thẻ. Đó là ĐÚNG, và lý do giống nhau ở cả ba: **sự việc không xảy ra được với họ**.
   * Người chưa thuộc nhóm nào thì không có vai để đổi, không có nhóm để đổi cài đặt, và không mở được phiếu trong nhóm
   * để mà bỏ dở (`ai_tasks.groupId` null chỉ đúng cho `ONB-CREATE-GROUP` — một trang không có bước nào để dở).
   *
   * 🔴 Vì sao KHÔNG mở `roles: ["*"]` cho gọn: `"*"` nghĩa là *"kể cả người chưa thuộc nhóm"*, và hai trong ba thẻ mang
   * `{tenNhom}`. Người không có nhóm thì `groupName === null` ⇒ `textRenderable` loại thẻ ⇒ ô vẫn im, nhưng im vì một
   * lý do KHÁC (không nói được tên) thay vì vì một lý do ĐÚNG (không có sự việc). Hai lý do trông giống nhau ở kết quả
   * và khác hẳn nhau ở ý nghĩa — gộp lại là đúng thứ hàng `OWE-NO-NAME` được thêm vào để tách ra.
   */
  { trigger: "MY_ROLE_CHANGED", role: null, decision: { kind: "structural", ref: "US-AIV2-0065" }, reason: "chưa thuộc nhóm nào thì không có vai trong nhóm để mà đổi — ô trống THẬT, không phải thiếu thẻ" },
  { trigger: "STALE", role: null, decision: { kind: "structural", ref: "US-AIV2-0065" }, reason: "phiếu dở đọc theo NHÓM hiệu lực (thẻ «Làm tiếp» của US-AIV2-tourguide-0007a mời phiếu của nhóm đó); người chưa thuộc nhóm nào không có nhóm hiệu lực ⇒ chưa mời phiếu tạo nhóm dở (nợ ghi ở README 0007)" },
  { trigger: "CONFIG_CHANGED", role: null, decision: { kind: "structural", ref: "US-AIV2-0065" }, reason: "chưa thuộc nhóm nào thì không có cài đặt nhóm nào đổi được cho người này thấy" },
]);

/** Ratchet ô trống — cùng luật một chiều với `MAX_SILENT_TRIGGERS`. */
/**
 * 🔴 0 → **3** ở `US-AIV2-0065` — lần thứ HAI ratchet này đi lên, và cùng một tính chất với lần 11→12 của
 * `MAX_SILENT_TRIGGERS`: **con số tăng vì SỰ THẬT được khai ra, không vì một khoảng trống mới sinh ra.**
 *
 * Ba ô (`MY_ROLE_CHANGED` · `STALE` · `CONFIG_CHANGED` × chưa-thuộc-nhóm) **không tồn tại trước commit này** — ba trigger
 * đó nằm trong `GUIDE_SILENT_TRIGGERS` nên PK-1 bỏ qua toàn bộ hàng của chúng. Nay chúng có thẻ, nên lưới soi tới từng
 * vai, và vai `null` lộ ra là một ô trống có lý do. Ròng lại: **5 trigger rời danh sách im lặng, 3 ô được khai chi
 * tiết hơn** — độ phân giải của phép đo tăng, không phải độ phủ giảm.
 *
 * ⚠️ Đừng cộng 7 + 3 rồi so với 12 + 0: hai danh sách đếm hai thứ khác nhau (trigger KHÔNG có thẻ nào vs ô một vai
 * không có thẻ), nên tổng của chúng không có nghĩa. Cùng lỗi "đếm là phép chiếu" mà CLAUDE.md §5 ghi.
 */
/**
 * 🔴 1 → 0 (QĐ PO 23/09, lối A). Ô duy nhất trong danh sách là `DUTY_CREATED × chưa-thuộc-nhóm`; nay cả trigger đó đã khai
 * im lặng ở `GUIDE_SILENT_TRIGGERS`, nên giữ lại một ô cho nó là **khai hai lần một khoảng trống** — và `coverage.invariants`
 * đòi mỗi ô khai ra phải CÒN THẬT (`hasCardFor === false` **và** trigger chưa im toàn phần), nên bản chép thừa sẽ mục.
 */
export const MAX_SILENT_COMBOS = 3;

/**
 * 🔴🔴 US-AIV2-0064-S02 (@UC0187) — CHIỀU THỨ BA: **trạng thái TÍN HIỆU**.
 *
 * `GUIDE_SILENT_COMBOS` ở trên đo (trigger × vai) — một ô ở đó "có người lo" chỉ nghĩa là **tồn tại** một thẻ khai đúng trigger
 * và mở cho đúng vai. Nó KHÔNG hỏi câu mà người dùng thật sự sống trong đó: *"ở trạng thái DB của tôi lúc này, thẻ đó có bắn
 * không?"* Hai câu hỏi khác nhau, và khoảng cách giữa chúng là toàn bộ `when[]`.
 *
 * Ví dụ đo được trên chính thư viện hôm nay: ô (`OPEN` × `member`) có `DUTY-MY-UNPAID` nên PK-1 xanh — nhưng một member ở nhóm
 * đang chạy mà **không nợ gì** thì `when: myUnpaidDueCount > 0` trượt, và trigger `OPEN` với vai `member` ra **rỗng**. Lưới cũ
 * không có chỗ nào nhìn thấy điều đó. Đây chính là lớp lỗi "đếm là PHÉP CHIẾU": tồn tại ≠ với tới được.
 *
 * ## Vì sao KHÔNG tích Descartes đầy đủ
 *
 * 10 tín hiệu, trong đó 8 là số không chặn trên ⇒ tập trạng thái là VÔ HẠN, và ngay cả khi rời rạc hoá thô (3 mốc mỗi tín hiệu)
 * thì 3^8 × 2 × 2 = 26.244 trạng thái × 17 trigger × 5 vai = **2,2 triệu ô**. Một lưới như thế vừa chạy lâu vừa **vô nghĩa**:
 * phần lớn trạng thái đó KHÔNG TỒN TẠI được (`fundCount: 3` mà `hasAnyGroup: false`), nên "ô mồ côi" của nó là ô không ai rơi
 * vào — đúng thứ làm ratchet mục dần rồi bị nới cho qua.
 *
 * ## Mốc được chọn, và LÝ DO từng mốc
 *
 * Mốc phải cắt đúng chỗ `when[]` của thư viện cắt — không thì phép đo mịn hơn hiện tượng, hoặc thô hơn. Đọc từ chính các vị-từ
 * đang có: `eq 0` · `gt 0` · `lte 1` · `gt 1` · `truthy`/`falsy`. ⇒ ba mốc cho số đếm là ĐỦ và CẦN:
 *  - `0`  — ranh giới `eq 0` / `gt 0` (SETUP-NO-FUND, DUTY-*, RECAP-ON-RETURN).
 *  - `1`  — ranh giới `lte 1` / `gt 1` (SETUP-NO-MEMBER: "mới có mình bạn"). Thiếu mốc này thì `memberCount` chỉ còn 0-hay-nhiều
 *           và cái bẫy "nhóm một người" biến mất khỏi lưới.
 *  - `3`  — đại diện ">1", chọn 3 thay vì 2 để không trùng với bất kỳ hằng nào trong `when[]` hôm nay: một fixture "tròn số"
 *           trùng ngưỡng làm vị-từ ĐÚNG và SAI ra cùng kết quả.
 * Boolean lấy cả hai giá trị. `role` là chiều riêng (đã có). `groupName` không rời rạc hoá thành mốc số — nó chỉ có hai trạng
 * thái NGHĨA: nói được tên (`textRenderable` qua) hay không; `PRESENT`/`ABSENT` phủ đúng hai trạng thái đó.
 *
 * ## Vì sao trạng thái phải TỰ NHẤT QUÁN
 *
 * Mỗi hàng dưới đây là một trạng thái **người dùng THẬT rơi vào được**, không phải một điểm trong lưới toán học. `hasAnyGroup:
 * false` ⇒ mọi phép đếm trong nhóm bằng 0 và `groupName` vắng, vì loader không có nhóm nào để đếm. Khai một trạng thái không
 * tồn tại rồi đòi thẻ cho nó là bắt thư viện trả lời một câu hỏi không ai hỏi — và một ô trống ở đó sẽ được "khai im lặng"
 * bằng một lý do rỗng, làm loãng chính danh sách mà ratchet đang đếm.
 *
 * `unknownSignals` cố ý KHÔNG vào lưới này: nó không phải trạng thái của người dùng mà là trạng thái của HỆ THỐNG (loader hỏng),
 * và luật ở đó là "im lặng đúng cách" — `signalsKnown()` khoá riêng ở `journey.test.ts`. Trộn hai thứ vào một lưới thì mọi ô
 * hỏng-loader đều thành "im lặng có lý do", và ratchet mất nghĩa.
 */
export interface GuideSignalState {
  /** Tên ngắn để thông báo lỗi nêu ĐÚNG ô, không phải một câu chung chung. */
  readonly id: string;
  readonly hasAnyGroup: boolean;
  readonly groupCount: number;
  readonly groupName: "PRESENT" | "ABSENT";
  readonly fundCount: number;
  readonly memberCount: number;
  readonly openCollectionCount: number;
  readonly myUnpaidDueCount: number;
  readonly myClaimedDueCount: number;
  readonly pendingConfirmations: number;
  /**
   * 🔴🔴 US-AIV2-0065 — NĂM CHIỀU MỚI, và vì sao chúng KHÔNG nhân vào tích Descartes.
   *
   * Năm tín hiệu của thư viện tình huống (`context.ts`) đều là **biến cố**, không phải trạng thái nền: chúng bằng
   * 0/false ở gần như mọi lượt, và chỉ một cái khác 0 tại một thời điểm. Nhân chúng vào 11 hàng có sẵn cho 11 × 2 × 4 ×
   * 4 × 2 × 4 = **5.632 hàng**, trong đó tuyệt đại đa số là trạng thái KHÔNG TỒN TẠI (việc vừa hỏng **và** bí 3 lượt
   * **và** vai vừa đổi **và** 2 phiếu dở — cùng một lượt). Đó đúng lớp "ô mồ côi của ô không ai rơi vào" mà docblock
   * `GUIDE_SIGNAL_STATES` đã cảnh báo, và nó làm ratchet mục dần.
   *
   * ⇒ Mặc định TẤT CẢ bằng 0/false trên 11 hàng cũ (đúng: đó là lượt bình thường), rồi thêm **đúng năm hàng** mới, mỗi
   * hàng bật ĐÚNG MỘT biến cố trên nền `RUNNING-CLEAN`. Nền sạch là có chủ đích: nó cô lập biến cố, nên một ô im ở
   * hàng đó chỉ có thể do chính thẻ của biến cố đó, không do một thẻ `DUTY` che mất.
   */
  readonly turnFailed: boolean;
  readonly turnsWithoutProgress: number;
  readonly unfinishedTaskCount: number;
  readonly myRoleChanged: boolean;
  readonly configChangedCount: number;
  /** US-AIV2-0062b (@UC0265): họ LƯỢT như `turnFailed`; bật ở ĐÚNG MỘT hàng (`BLOCKED-NO-SEAT`). */
  readonly askedGroupNoSeat: boolean;
  /**
   * 🔴 US-AIV2-0111 C07g (@UC0216) — hàng chờ duyệt vào nhóm: của CHÍNH người này (`myJoinPendingCount` + nói được tên nhóm chờ hay
   * không) và đang chờ NGƯỜI NÀY duyệt (`pendingJoinRequests`). Bằng 0/ABSENT trên mọi hàng cũ (`NO_JOIN_QUEUE`); ba hàng mới bật từng cái.
   */
  readonly myJoinPendingCount: number;
  readonly pendingJoinGroup: "PRESENT" | "ABSENT";
  readonly pendingJoinRequests: number;
  /** 🔴 US-AIV2-0113 M05 (@UC0214) — lời mời đích danh đang chờ CHÍNH người này (+ nói được tên nhóm hay không). 0/ABSENT trên mọi hàng cũ. */
  readonly myInviteCount: number;
  readonly pendingInvite: "PRESENT" | "ABSENT";
  /**
   * 🔴 US-AIV2-0112 H05 (@UC0322) — bốn chiều của lời chào có số. Mặc định "vắng" trên mọi hàng cũ (`NO_MONEY_FACT`) trừ hàng có nợ
   * (`OWE-*`, `BUSY-ALL` — loader thật nạp fact khoản cùng hàng với số đếm); bốn hàng mới bật từng chiều trên nền `RUNNING-CLEAN`.
   */
  readonly myNextDue: "PRESENT" | "ABSENT";
  readonly myOpenDuesSettled: boolean;
  /** Đợt đang mở theo cửa GUI tiến độ: còn người NHẮC ĐƯỢC · không còn ai nhắc được (đủ / còn lại toàn đã báo) · không nạp. */
  readonly openCollectionProgress: "UNPAID" | "FULL" | "ABSENT";
  readonly bankAccountMissing: boolean;
}

/** Nền "lượt bình thường": không biến cố nào. 11 hàng cũ đều dùng, nên thêm chiều thứ sáu chỉ sửa MỘT chỗ. */
const NO_EVENT = Object.freeze({
  turnFailed: false,
  turnsWithoutProgress: 0,
  unfinishedTaskCount: 0,
  myRoleChanged: false,
  configChangedCount: 0,
  askedGroupNoSeat: false,
});

/** Nền "không ai chờ duyệt": 0 lời xin của tôi, 0 lời xin chờ tôi. Hàng cũ dùng chung (thêm chiều mới chỉ sửa MỘT chỗ). */
const NO_JOIN_QUEUE = Object.freeze({ myJoinPendingCount: 0, pendingJoinGroup: "ABSENT", pendingJoinRequests: 0, myInviteCount: 0, pendingInvite: "ABSENT" } as const);

/** Nền "không fact tiền nào" (H05) — hàng cũ dùng chung; thêm chiều mới chỉ sửa MỘT chỗ. */
const NO_MONEY_FACT = Object.freeze({ myNextDue: "ABSENT", myOpenDuesSettled: false, openCollectionProgress: "ABSENT", bankAccountMissing: false } as const);

/**
 * Tập trạng thái ĐẠI DIỆN — mỗi hàng là một chặng có thật trong vòng đời một nhóm, đặt tên theo chặng đó.
 *
 * `NO-GROUP` → `GROUP-EMPTY` → `FUND-ONLY` → `MEMBERS-NO-COLLECTION` → `RUNNING-CLEAN` là thang khởi tạo (đúng thứ tự mà
 * `SETUP-*` cắt). Ba hàng `OWE-*` / `CLAIMED-*` / `CONFIRM-*` là các trạng thái nghĩa vụ chồng lên một nhóm đang chạy.
 * `RUNNING-NO-NAME` là ca `groupName` vắng mà nhóm vẫn chạy — trạng thái THẬT (loader fail-soft từng phép đếm), và là ca duy
 * nhất làm `textRenderable` từ chối một thẻ đã qua `when[]`.
 */
export const GUIDE_SIGNAL_STATES: readonly GuideSignalState[] = Object.freeze<GuideSignalState[]>([
  { id: "NO-GROUP", hasAnyGroup: false, groupCount: 0, groupName: "ABSENT", fundCount: 0, memberCount: 0, openCollectionCount: 0, myUnpaidDueCount: 0, myClaimedDueCount: 0, pendingConfirmations: 0, ...NO_EVENT, ...NO_JOIN_QUEUE, ...NO_MONEY_FACT },
  { id: "GROUP-EMPTY", hasAnyGroup: true, groupCount: 1, groupName: "PRESENT", fundCount: 0, memberCount: 1, openCollectionCount: 0, myUnpaidDueCount: 0, myClaimedDueCount: 0, pendingConfirmations: 0, ...NO_EVENT, ...NO_JOIN_QUEUE, ...NO_MONEY_FACT },
  { id: "FUND-ONLY", hasAnyGroup: true, groupCount: 1, groupName: "PRESENT", fundCount: 1, memberCount: 1, openCollectionCount: 0, myUnpaidDueCount: 0, myClaimedDueCount: 0, pendingConfirmations: 0, ...NO_EVENT, ...NO_JOIN_QUEUE, ...NO_MONEY_FACT },
  { id: "MEMBERS-NO-COLLECTION", hasAnyGroup: true, groupCount: 1, groupName: "PRESENT", fundCount: 1, memberCount: 3, openCollectionCount: 0, myUnpaidDueCount: 0, myClaimedDueCount: 0, pendingConfirmations: 0, ...NO_EVENT, ...NO_JOIN_QUEUE, ...NO_MONEY_FACT },
  { id: "RUNNING-CLEAN", hasAnyGroup: true, groupCount: 1, groupName: "PRESENT", fundCount: 1, memberCount: 3, openCollectionCount: 1, myUnpaidDueCount: 0, myClaimedDueCount: 0, pendingConfirmations: 0, ...NO_EVENT, ...NO_JOIN_QUEUE, ...NO_MONEY_FACT },
  { id: "RUNNING-NO-NAME", hasAnyGroup: true, groupCount: 1, groupName: "ABSENT", fundCount: 1, memberCount: 3, openCollectionCount: 1, myUnpaidDueCount: 0, myClaimedDueCount: 0, pendingConfirmations: 0, ...NO_EVENT, ...NO_JOIN_QUEUE, ...NO_MONEY_FACT },
  /**
   * 🔴 `OWE-NO-NAME` KHÔNG phải một hàng cho đủ bộ — nó là hàng DUY NHẤT phân biệt được hai lý do im lặng.
   *
   * Đo được lúc dựng lưới: ở `RUNNING-NO-NAME`, mọi tín hiệu nghĩa vụ đều bằng 0, nên 16 ô im lặng của nó **đã được luật
   * "không còn nghĩa vụ" giải thích hết** — miễn trừ "không nói được tên nhóm" chưa bao giờ gánh một ô nào. Một miễn trừ
   * không bao giờ dùng tới là một miễn trừ không ai kiểm: nó ngồi đó cho tới ngày `0065` đổ nội dung thật vào, rồi âm thầm
   * nuốt một ô đáng lẽ phải đỏ.
   *
   * Hàng này ép hai lý do tách bạch: CÓ nghĩa vụ (`myUnpaidDueCount: 3`) mà KHÔNG có tên nhóm. Thẻ `DUTY-MY-UNPAID` không
   * mang `{tenNhom}` nên vẫn bắn ⇒ ô LOUD ⇒ lưới khẳng định đúng điều cần: **thiếu tên nhóm không được làm câm một lời nhắc
   * về khoản phải đóng.** Ngày nào ai đó thêm `{tenNhom}` vào câu đó, hàng này đỏ trước khi người dùng im.
   */
  { id: "OWE-NO-NAME", hasAnyGroup: true, groupCount: 1, groupName: "ABSENT", fundCount: 1, memberCount: 3, openCollectionCount: 1, myUnpaidDueCount: 3, myClaimedDueCount: 0, pendingConfirmations: 0, ...NO_EVENT, ...NO_JOIN_QUEUE, ...NO_MONEY_FACT, myNextDue: "PRESENT" },
  { id: "OWE-MINE", hasAnyGroup: true, groupCount: 1, groupName: "PRESENT", fundCount: 1, memberCount: 3, openCollectionCount: 1, myUnpaidDueCount: 3, myClaimedDueCount: 0, pendingConfirmations: 0, ...NO_EVENT, ...NO_JOIN_QUEUE, ...NO_MONEY_FACT, myNextDue: "PRESENT" },
  { id: "CLAIMED-MINE", hasAnyGroup: true, groupCount: 1, groupName: "PRESENT", fundCount: 1, memberCount: 3, openCollectionCount: 1, myUnpaidDueCount: 0, myClaimedDueCount: 1, pendingConfirmations: 0, ...NO_EVENT, ...NO_JOIN_QUEUE, ...NO_MONEY_FACT },
  { id: "CONFIRM-WAITING", hasAnyGroup: true, groupCount: 1, groupName: "PRESENT", fundCount: 1, memberCount: 3, openCollectionCount: 1, myUnpaidDueCount: 0, myClaimedDueCount: 0, pendingConfirmations: 3, ...NO_EVENT, ...NO_JOIN_QUEUE, ...NO_MONEY_FACT },
  { id: "BUSY-ALL", hasAnyGroup: true, groupCount: 3, groupName: "PRESENT", fundCount: 3, memberCount: 3, openCollectionCount: 3, myUnpaidDueCount: 3, myClaimedDueCount: 1, pendingConfirmations: 3, ...NO_EVENT, ...NO_JOIN_QUEUE, ...NO_MONEY_FACT, myNextDue: "PRESENT", openCollectionProgress: "UNPAID", bankAccountMissing: true },
  /**
   * ═══ US-AIV2-0065 — NĂM HÀNG BIẾN CỐ. Mỗi hàng bật ĐÚNG MỘT biến cố trên nền `RUNNING-CLEAN` ═══
   *
   * Nền `RUNNING-CLEAN` (nhóm chạy, 0 nghĩa vụ) là chọn CÓ CHỦ ĐÍCH: ở nền đó mọi thẻ `DUTY`/`SETUP` đều im, nên một ô
   * NÓI ở hàng này chỉ có thể do chính thẻ của biến cố. Đặt biến cố trên nền `BUSY-ALL` thì thẻ `DUTY` (bậc cao hơn)
   * sẽ thắng và nuốt mất phép đo — ô vẫn xanh, nhưng xanh vì một LÝ DO KHÁC. Đó đúng lớp lỗi "xanh vì lý do khác" mà
   * `journey.ts` đã ghi lại ở vế `textRenderable`.
   *
   * 🔴 Ba hàng cuối (`ROLE-CHANGED` · `UNFINISHED` · `CONFIG-CHANGED`) có `groupName: "PRESENT"` vì hai trong ba thẻ mang
   * `{tenNhom}`; thiếu tên thì `textRenderable` loại thẻ và ô im — một ô im ĐÚNG luật nhưng đo nhầm thứ.
   */
  { id: "TURN-FAILED", hasAnyGroup: true, groupCount: 1, groupName: "PRESENT", fundCount: 1, memberCount: 3, openCollectionCount: 1, myUnpaidDueCount: 0, myClaimedDueCount: 0, pendingConfirmations: 0, ...NO_EVENT, ...NO_JOIN_QUEUE, ...NO_MONEY_FACT, turnFailed: true },
  // 3 = ĐÚNG ngưỡng PO chốt. Chọn đúng biên thay vì 5 là có chủ: nếu ai đó siết `gte 3` thành `gt 3`, hàng này ĐỎ ngay.
  { id: "STUCK-3-TURNS", hasAnyGroup: true, groupCount: 1, groupName: "PRESENT", fundCount: 1, memberCount: 3, openCollectionCount: 1, myUnpaidDueCount: 0, myClaimedDueCount: 0, pendingConfirmations: 0, ...NO_EVENT, ...NO_JOIN_QUEUE, ...NO_MONEY_FACT, turnsWithoutProgress: 3 },
  { id: "ROLE-CHANGED", hasAnyGroup: true, groupCount: 1, groupName: "PRESENT", fundCount: 1, memberCount: 3, openCollectionCount: 1, myUnpaidDueCount: 0, myClaimedDueCount: 0, pendingConfirmations: 0, ...NO_EVENT, ...NO_JOIN_QUEUE, ...NO_MONEY_FACT, myRoleChanged: true },
  { id: "UNFINISHED", hasAnyGroup: true, groupCount: 1, groupName: "PRESENT", fundCount: 1, memberCount: 3, openCollectionCount: 1, myUnpaidDueCount: 0, myClaimedDueCount: 0, pendingConfirmations: 0, ...NO_EVENT, ...NO_JOIN_QUEUE, ...NO_MONEY_FACT, unfinishedTaskCount: 1 },
  { id: "CONFIG-CHANGED", hasAnyGroup: true, groupCount: 1, groupName: "PRESENT", fundCount: 1, memberCount: 3, openCollectionCount: 1, myUnpaidDueCount: 0, myClaimedDueCount: 0, pendingConfirmations: 0, ...NO_EVENT, ...NO_JOIN_QUEUE, ...NO_MONEY_FACT, configChangedCount: 2 },
  // 🔴 US-AIV2-0062b: người không có ghế ở nhóm đang hỏi, trên nền sạch — cùng lý do cô lập biến cố như năm hàng trên.
  // ⚠️ review #970 L-2: hàng này chỉ phủ ô O/A/T/M. Hình dạng THẬT của `noDoorHere` (vai `null` + `hasAnyGroup` true) bị `consistent()`
  // loại khỏi lưới — hình dạng đó được khoá ở `blocked-detour.test.ts` (quét cả vai null) và `journey-context.loader.spec.ts` (loader thật).
  { id: "BLOCKED-NO-SEAT", hasAnyGroup: true, groupCount: 1, groupName: "PRESENT", fundCount: 1, memberCount: 3, openCollectionCount: 1, myUnpaidDueCount: 0, myClaimedDueCount: 0, pendingConfirmations: 0, ...NO_EVENT, ...NO_JOIN_QUEUE, ...NO_MONEY_FACT, askedGroupNoSeat: true },
  /**
   * ═══ US-AIV2-0111 C07g (@UC0216) — BA HÀNG HÀNG-CHỜ-DUYỆT ═══
   * `JOIN-PENDING-NO-GROUP` là ca `dung` (E2E 28/09): 0 nhóm hoạt động, đúng MỘT lời xin đang chờ ⇒ vai `null` phải NÓI (không im, không
   * "Bạn chưa có nhóm nào"). `JOIN-PENDING-MANY` ≥2 lời xin ⇒ nói số. `JOIN-REQUESTS-WAITING` = nhóm chạy sạch + 3 lời xin chờ duyệt ⇒
   * chủ nhóm/quản trị phải NÓI; thủ quỹ/member im ĐÚNG (không bấm duyệt được — `hasDutyFor` của lưới tách đúng vai).
   */
  { id: "JOIN-PENDING-NO-GROUP", hasAnyGroup: false, groupCount: 0, groupName: "ABSENT", fundCount: 0, memberCount: 0, openCollectionCount: 0, myUnpaidDueCount: 0, myClaimedDueCount: 0, pendingConfirmations: 0, ...NO_EVENT, ...NO_JOIN_QUEUE, ...NO_MONEY_FACT, myJoinPendingCount: 1, pendingJoinGroup: "PRESENT" },
  { id: "JOIN-PENDING-MANY", hasAnyGroup: false, groupCount: 0, groupName: "ABSENT", fundCount: 0, memberCount: 0, openCollectionCount: 0, myUnpaidDueCount: 0, myClaimedDueCount: 0, pendingConfirmations: 0, ...NO_EVENT, ...NO_JOIN_QUEUE, ...NO_MONEY_FACT, myJoinPendingCount: 2 },
  { id: "JOIN-REQUESTS-WAITING", hasAnyGroup: true, groupCount: 1, groupName: "PRESENT", fundCount: 1, memberCount: 3, openCollectionCount: 1, myUnpaidDueCount: 0, myClaimedDueCount: 0, pendingConfirmations: 0, ...NO_EVENT, ...NO_JOIN_QUEUE, ...NO_MONEY_FACT, pendingJoinRequests: 3 },
  /**
   * ═══ US-AIV2-0113 M05 (@UC0214) — HÀNG LỜI MỜI ═══ `khach` (E2E 28/09): 0 nhóm, đúng MỘT lời mời email chưa nhận ⇒ vai `null` phải NÓI lời
   * mời (không im, không "Bạn chưa có nhóm nào").
   */
  { id: "INVITE-PENDING-NO-GROUP", hasAnyGroup: false, groupCount: 0, groupName: "ABSENT", fundCount: 0, memberCount: 0, openCollectionCount: 0, myUnpaidDueCount: 0, myClaimedDueCount: 0, pendingConfirmations: 0, ...NO_EVENT, ...NO_JOIN_QUEUE, ...NO_MONEY_FACT, myInviteCount: 1, pendingInvite: "PRESENT" },
  /**
   * ═══ US-AIV2-0112 H05 (@UC0322) — BỐN HÀNG LỜI CHÀO CÓ SỐ, mỗi hàng bật ĐÚNG MỘT chiều trên nền `RUNNING-CLEAN` ═══
   * `COLLECTING-UNPAID`: đợt đang mở còn người chưa đóng ⇒ người quản lý phải NÓI (tiến độ), thành viên im ĐÚNG (không nghe số cả đợt).
   * `COLLECTING-FULL`: đợt đã thu đủ ⇒ im mọi vai (không nút Nhắc cho một đợt không còn ai để nhắc).
   * `NO-BANK-ACCOUNT`: đang thu mà chưa có STK ⇒ chủ nhóm/quản trị NÓI, thủ quỹ/thành viên im ĐÚNG (không đổi được STK).
   * `ALL-PAID`: mọi khoản của tôi đã được xác nhận ⇒ NÓI "đã đóng đủ" (bậc IDLE).
   */
  { id: "COLLECTING-UNPAID", hasAnyGroup: true, groupCount: 1, groupName: "PRESENT", fundCount: 1, memberCount: 3, openCollectionCount: 1, myUnpaidDueCount: 0, myClaimedDueCount: 0, pendingConfirmations: 0, ...NO_EVENT, ...NO_JOIN_QUEUE, ...NO_MONEY_FACT, openCollectionProgress: "UNPAID" },
  { id: "COLLECTING-FULL", hasAnyGroup: true, groupCount: 1, groupName: "PRESENT", fundCount: 1, memberCount: 3, openCollectionCount: 1, myUnpaidDueCount: 0, myClaimedDueCount: 0, pendingConfirmations: 0, ...NO_EVENT, ...NO_JOIN_QUEUE, ...NO_MONEY_FACT, openCollectionProgress: "FULL" },
  { id: "NO-BANK-ACCOUNT", hasAnyGroup: true, groupCount: 1, groupName: "PRESENT", fundCount: 1, memberCount: 3, openCollectionCount: 1, myUnpaidDueCount: 0, myClaimedDueCount: 0, pendingConfirmations: 0, ...NO_EVENT, ...NO_JOIN_QUEUE, ...NO_MONEY_FACT, bankAccountMissing: true },
  { id: "ALL-PAID", hasAnyGroup: true, groupCount: 1, groupName: "PRESENT", fundCount: 1, memberCount: 3, openCollectionCount: 1, myUnpaidDueCount: 0, myClaimedDueCount: 0, pendingConfirmations: 0, ...NO_EVENT, ...NO_JOIN_QUEUE, ...NO_MONEY_FACT, myOpenDuesSettled: true },
]);

/**
 * Ô (trigger × vai × trạng thái) IM LẶNG có khai — chiều thứ ba của `GUIDE_SILENT_COMBOS`.
 *
 * 🔴 Khác ô hai chiều ở một điểm quyết định: ô ở đây được đo bằng **`journey()` chạy thật**, không bằng phép tồn tại. Nên "im
 * lặng" ở đây là im lặng mà NGƯỜI DÙNG nghe thấy, và lý do khai phải trả lời được câu *"người ở trạng thái này có việc gì phải
 * làm không"* — nếu có mà ta im, đó là lỗi nội dung (`0065`), không phải một ô hợp lệ.
 */
export interface GuideSilentCell {
  readonly trigger: GuideTrigger;
  readonly role: AiPageGroupRole | null;
  /** `id` của một hàng trong `GUIDE_SIGNAL_STATES`. */
  readonly state: string;
  readonly reason: string;
}

/**
 * 🔴 DANH SÁCH RỖNG LÀ MỘT LỜI KHAI, không phải một chỗ chưa làm.
 *
 * Mọi ô im lặng hôm nay đều đã có địa chỉ ở một trong hai danh sách trên (`GUIDE_SILENT_TRIGGERS` cho trigger chưa có thẻ,
 * `GUIDE_SILENT_COMBOS` cho ô trigger × vai) **hoặc** thoả đúng MỘT luật miễn trừ, khai bằng mã ở `coverage.invariants.spec.ts`
 * §PK-5: *"mọi tín hiệu nghĩa vụ mà vai này với tới được đều bằng 0"* — một vị-từ KHẲNG ĐỊNH trên dữ liệu, không phải một
 * nhóm định nghĩa bằng phủ định. Ô nào ngoài ba đường đó ⇒ ĐỎ, và thông báo nêu đúng `trigger|vai|trạng-thái`.
 */
export const GUIDE_SILENT_CELLS: readonly GuideSilentCell[] = Object.freeze<GuideSilentCell[]>([]);

/** Ratchet chiều ba — cùng luật một chiều, cùng kỷ luật `toBe` với hai ratchet trên. */
export const MAX_SILENT_CELLS = 0;

/**
 * Trang mà THƯ VIỆN THẺ có thể trỏ tới — suy TỪ thư viện, không khai tay.
 *
 * ⚠️ **L-1 (review #835), có địa chỉ `US-AIV2-0065`:** danh sách này PHẲNG theo TOÀN thư viện, nên mỗi lượt `done`/`reply` hỏi cổng
 * thêm cả `READ-PEOPLE-MINE` — trang chỉ phục vụ trigger `BLOCKED`. Vô hại hôm nay (7 thẻ) nhưng nó lớn **1:1 theo số thẻ**, và
 * `0065` là lúc đổ nội dung thật vào. Khi đó: suy theo TRIGGER (chỉ trang của thẻ có thể bắn ở lượt này) và đặt một ratchet
 * `toBe(N)` cùng kỷ luật với `MAX_SILENT_TRIGGERS` — trần hiện tại (`toBeLessThan(AI_CAPABILITY_REGISTRY.length)`) gần như vô nghĩa.
 *
 * Dùng ở một chỗ: tầng api cần biết phải hỏi cổng năng lực cho những trang nào trước khi gọi `journey()` ở đường "việc xong, làm gì tiếp"
 * (hỏi cổng cho TOÀN BỘ Sổ mỗi lượt là +N truy vấn cho một câu gợi ý). Khai tay danh sách này là bản chép thứ hai của thư viện — thêm một
 * thẻ trỏ trang mới mà quên sửa danh sách thì thẻ đó im lặng không bao giờ hiện, đúng lớp lỗi mà `GUIDE_SILENT_TRIGGERS` sinh ra để chặn.
 */
export const GUIDE_CARD_CTA_PAGES: readonly string[] = Object.freeze([
  ...new Set(GUIDE_CARD_LIBRARY.flatMap((c) => (c.cta ? [c.cta.page] : []))),
]);

/**
 * 🔴 US-AIV2-0062-S01 (@UC0183) — MIỀN KHOÁ của `ai_user_nudge_state.nudge_id`, SUY từ thư viện.
 *
 * Bảng đó (US-AI-0442) khai hợp đồng `nudgeId = Flow.id trong FLOW_CATALOG` — mà `FLOW_CATALOG` **đã chết cùng engine v1** (ADR-0009).
 * 0062 đổi miền sang **`guideNudgeKey(GuideCard.id, groupId)`** (`nudge.ts`) và dọn sạch hàng cũ trong cùng PR (migration
 * `…_ai_nudge_state_guide_card_ids`), vì để lại thì một `GuideCard.id` trùng tên một `Flow.id` cũ sẽ **kế thừa trạng thái đã-bỏ-qua của
 * người dùng** — im lặng, đúng lớp lỗi "khoá trùng miền khác nghĩa". Tập dưới đây là miền của **phần id** trong khoá đó, không phải cả khoá.
 *
 * Danh sách suy TỪ thư viện: một bản chép tay ở đây sẽ mục ngay lần 0065 đổ nội dung thật vào, và thứ trôi ra là một lời nhắc ghi vào
 * bảng dưới một id không ai tra ngược được.
 */
export const GUIDE_CARD_IDS: ReadonlySet<string> = Object.freeze(new Set(GUIDE_CARD_LIBRARY.map((c) => c.id)));

/**
 * 🔴 Hình của một id thẻ — CHỮ HOA · SỐ · gạch nối, KHÔNG gì khác (review #835 M-1).
 *
 * Đây không phải thẩm mỹ: khoá *"đã nhắc rồi"* là `guideNudgeKey(cardId, groupId)` = `id@nhóm`, nên **ký tự `@` không được
 * xuất hiện trong id** — nếu có, phép tách ngược sẽ cắt nhầm và một thẻ im ở nhóm này có thể đọc thành thẻ khác. Ràng buộc ở
 * đây biến điều đó từ *"chắc là không ai đặt tên thế"* thành một phép đo (`coverage.invariants.spec.ts`), và nó sẽ đỏ đúng lúc
 * `US-AIV2-0065` đổ nội dung thật vào thư viện.
 */
export const GUIDE_CARD_ID_RE = /^[A-Z0-9-]+$/;
